export type Language =
  | 'en'
  | 'ru'
  | 'ua'
  | 'de'
  | 'es'
  | 'fr'
  | 'pt-br'
  | 'it'
  | 'nl'

export const supportedLanguages: {
  code: Language
  name: string
  nativeName: string
  path: string
}[] = [
  { code: 'en', name: 'English', nativeName: 'English', path: '/' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', path: '/ru' },
  { code: 'ua', name: 'Ukrainian', nativeName: 'Українська', path: '/ua' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', path: '/de' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', path: '/es' },
  { code: 'fr', name: 'French', nativeName: 'Français', path: '/fr' },
  {
    code: 'pt-br',
    name: 'Portuguese (BR)',
    nativeName: 'Português',
    path: '/pt-br',
  },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', path: '/it' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', path: '/nl' },
]

const svcMeta = [
  {
    domain: 'wix.com',
    rating: 4.9,
    link: 'https://www.wix.com/ai-website-builder',
  },
  { domain: 'framer.com', rating: 4.8, link: 'https://www.framer.com/ai/' },
  {
    domain: 'durable.co',
    rating: 4.7,
    link: 'https://durable.com/ai-website-builder',
  },
  {
    domain: 'hostinger.com',
    rating: 4.6,
    link: 'https://www.hostinger.com/ai-website-builder',
  },
  {
    domain: '10web.io',
    rating: 4.6,
    link: 'https://10web.io/ai-website-builder/',
  },
]

function svc(
  types: string[],
  bestFors: string[],
  prosArr: string[][],
  consArr: string[],
  speeds: string[],
  seos: string[],
  flexes: string[],
  integs: string[],
  diffs: string[],
) {
  return [
    {
      name: 'Wix AI',
      ...svcMeta[0],
      type: types[0],
      bestFor: bestFors[0],
      pros: prosArr[0],
      cons: consArr[0],
      speed: speeds[0],
      seo: seos[0],
      flexibility: flexes[0],
      integrations: integs[0],
      difficulty: diffs[0],
    },
    {
      name: 'Framer AI',
      ...svcMeta[1],
      type: types[1],
      bestFor: bestFors[1],
      pros: prosArr[1],
      cons: consArr[1],
      speed: speeds[1],
      seo: seos[1],
      flexibility: flexes[1],
      integrations: integs[1],
      difficulty: diffs[1],
    },
    {
      name: 'Durable AI',
      ...svcMeta[2],
      type: types[2],
      bestFor: bestFors[2],
      pros: prosArr[2],
      cons: consArr[2],
      speed: speeds[2],
      seo: seos[2],
      flexibility: flexes[2],
      integrations: integs[2],
      difficulty: diffs[2],
    },
    {
      name: 'Hostinger AI',
      ...svcMeta[3],
      type: types[3],
      bestFor: bestFors[3],
      pros: prosArr[3],
      cons: consArr[3],
      speed: speeds[3],
      seo: seos[3],
      flexibility: flexes[3],
      integrations: integs[3],
      difficulty: diffs[3],
    },
    {
      name: '10Web AI',
      ...svcMeta[4],
      type: types[4],
      bestFor: bestFors[4],
      pros: prosArr[4],
      cons: consArr[4],
      speed: speeds[4],
      seo: seos[4],
      flexibility: flexes[4],
      integrations: integs[4],
      difficulty: diffs[4],
    },
  ]
}

// ═══════════════════════════════════════════════════════
// ENGLISH
// ═══════════════════════════════════════════════════════
const en = {
  brand: 'Magic AI Builders',
  seo: {
    title: 'Best AI Website Builders — Reviews, Comparison & Pricing',
    description:
      'Discover and compare AI website builders. Independent reviews, key features, pricing, pros and cons — choose the best builder for your website.',
  },
  nav: {
    rating: 'Rating',
    comparison: 'Comparison',
    howItWorks: 'How It Works',
    faq: 'FAQ',
    start: 'Review Services',
  },
  hero: {
    h1: 'Best AI Website Builders: Rating & Comparison',
    subtitle:
      'We compare website builders with AI: which is easiest to use, which gives the best results, and which fits your specific needs. Short, practical, based on real tests.',
    description1:
      'We test website builders with AI hands-on and rate them by ease of use, design quality, SEO tools, pricing, and publishing speed.',
    description2: '',
    cta: 'View Rating',
    compare: 'Compare Builders',
  },
  rankingCovers: { title: '', description: '', bullets: [] as string[] },
  whatIsAi: {
    title: 'Why People Build Websites with AI',
    subtitle:
      'Website builders with AI became popular because they are fast, simple, and accessible for anyone',
    structure: {
      title: 'Ready in Minutes',
      desc: 'AI generates a first draft from a short prompt — no weeks of waiting.',
    },
    content: {
      title: 'Cheaper Than a Studio',
      desc: 'Affordable to start — pricing depends on the plan.',
    },
    edit: {
      title: 'Simple and Clear',
      desc: 'Edit visually with a simple no-code editor.',
    },
    hosting: {
      title: 'For Most Website Needs',
      desc: 'Service sites, portfolios, business pages, multi-page websites — AI covers most common use cases.',
    },
  },
  howItWorks: {
    title: 'How AI Creates Your Website',
    subtitle:
      'AI assembles structure, copy, and design in minutes — you just refine the details and publish your site.',
    steps: [
      {
        title: 'Describe the site you need',
        desc: 'Specify your business type, target audience, and website goals',
      },
      {
        title: 'AI generates a ready site',
        desc: 'The builder creates design, copy, and structure in minutes',
      },
      {
        title: 'Make it yours',
        desc: 'Edit text, images, and style through a visual editor',
      },
      {
        title: 'Connect domain & launch',
        desc: 'Link your web address and your site goes live instantly',
      },
    ],
  },
  topServices: {
    title: 'Best AI Website Builders Rating',
    subtitle:
      'Editorial rating using a unified methodology: we test services hands-on and evaluate the results.',
    footerText:
      'We build a test site in each builder and compare usability, output quality, and editing capabilities. We also evaluate SEO settings, integrations, and value for money. The final score is editorial, based on identical criteria.',
    ratingDisclaimer:
      'Editorial score based on features, usability, and value. Not user reviews.',
    visitSite: 'Try Free',
    labels: {
      bestFor: 'Best for:',
      pros: 'Pros:',
      cons: 'Cons:',
      details: 'Show details',
      hide: 'Hide details',
      seo: 'SEO Optimization',
      flexibility: 'Flexibility',
      integrations: 'Integrations',
      speed: 'Creation Speed',
      difficulty: 'Creation Difficulty',
    },
    services: svc(
      ['Universal', 'Design', 'Speed', 'Value', 'WordPress'],
      [
        'Universal Business',
        'Portfolios & Landing Pages',
        'Local Services',
        'Budget-Friendly Start',
        'WordPress Sites',
      ],
      [
        ['High flexibility', 'Strong SEO', 'Many integrations'],
        ['Max flexibility', 'Good SEO', 'Medium integrations'],
        ['Fast generation', 'Basic SEO', 'Basic integrations'],
        ['Medium flexibility', 'Medium SEO', 'Medium integrations'],
        ['High flexibility', 'Strong SEO', 'Many integrations'],
      ],
      [
        'Can be overwhelming for simple sites',
        'Steeper learning curve',
        'Limited design customization',
        'Templates can feel generic',
        'Requires WP knowledge for deep edits',
      ],
      ['10–30 min', '10–40 min', '5–15 min', '10–30 min', '30–90 min'],
      ['Strong', 'Good', 'Basic', 'Medium', 'Strong'],
      ['High', 'Max', 'Low', 'Medium', 'High'],
      ['Many', 'Medium', 'Basic', 'Medium', 'Many'],
      ['Easy', 'Hard', 'Easy', 'Easy', 'Medium'],
    ),
  },
  servicesTable: {
    title: 'Compare Top AI Website Builders',
    subtitle:
      'Editorial rating using a unified methodology: we test services hands-on and evaluate the results.',
    footerText:
      'We build a test site in each builder and compare usability, output quality, and editing capabilities.',
    service: 'Service',
    type: 'Website Type',
    speed: 'Speed',
    seo: 'SEO',
    flexibility: 'Flexibility',
    integrations: 'Integrations',
    price: 'Price Range',
  },
  comparison: {
    title: 'AI Website Builder vs Web Studio: Which to Choose',
    subtitle:
      'A factual comparison by timeline, cost, revisions, and control over the result.',
    intro: '',
    feature: 'Feature',
    studio: 'Web Studio',
    ai: 'AI Website Builder',
    rows: {
      time: { name: 'Time to Launch', studio: '2–8 weeks', ai: '5–30 minutes' },
      cost: {
        name: 'Typical Cost',
        studio: '$2,000–$10,000+',
        ai: '$0–$30/mo',
      },
      revisions: {
        name: 'Revisions',
        studio: 'Paid or limited',
        ai: 'Unlimited, instant',
      },
      maintenance: {
        name: 'Ongoing Support',
        studio: 'Separate contract',
        ai: 'Usually included',
      },
      control: {
        name: 'Content Control',
        studio: 'Through developer',
        ai: 'Full self-service',
      },
    },
  },
  methodology: {
    label: 'Trust & Transparency',
    title: 'How We Evaluate AI Website Builders',
    description:
      "We don't just rewrite marketing claims. We actually test each AI website builder: creating sites, checking ease of use, and comparing results side by side.",
    criteria: [] as any[],
    disclaimer: '',
    cards: [
      {
        title: 'Hands-on Testing',
        desc: 'We build real sites and compare results across every platform',
      },
      {
        title: 'Consistent Criteria',
        desc: 'Every AI website builder is evaluated on the same set of factors',
      },
      {
        title: 'Pros & Cons, No Fluff',
        desc: 'Honest assessments with clear strengths and weaknesses',
      },
      {
        title: 'Unbiased Rating',
        desc: 'Rankings reflect quality, not sponsorships',
      },
    ],
  },
  faq: {
    title: 'Frequently Asked Questions About AI Website Builders',
    subtitle: 'Everything you need to know about building websites with AI',
    items: [
      {
        q: 'What is an AI website builder?',
        a: 'An AI website builder creates a website draft from a short prompt. It generates structure, basic copy, and design, then you customize and publish it.\n\n• Builds pages like Home, About, Services, Contact\n• No-code visual editor for changes\n• Fast setup compared to manual building\n• Good for quick launches and testing ideas',
      },
      {
        q: 'How does an AI website builder create a website?',
        a: 'Most AI builders follow the same flow: you describe your project, AI generates a draft, and you refine it before publishing.\n\n• Enter niche, goals, and style preferences\n• AI generates layout, sections, and text\n• You edit blocks, fonts, images, and navigation\n• Connect a domain and publish',
      },
      {
        q: 'Do I need coding or design skills to use an AI website builder?',
        a: "No. AI website builders are made for beginners and work with a visual, no-code editor.\n\n• Edit text and sections like a page builder\n• Adjust styles without programming\n• You'll get better results with clear input (goal, audience, tone)",
      },
      {
        q: 'Are AI-built websites good for SEO?',
        a: 'Yes — if you polish the content and basic SEO settings. AI helps you start faster, but ranking still depends on quality and structure.\n\n• Unique page titles and meta descriptions\n• Clear H1/H2 headings and internal links\n• Helpful, non-duplicate content\n• Fast loading and mobile usability',
      },
      {
        q: 'Can I create a multi-page website with AI?',
        a: 'Yes. Most AI website builders can generate multi-page sites with navigation and standard pages.\n\n• Home, About, Services, Contact, Blog (depending on builder)\n• Menu and internal linking\n• Easy page editing and updates after launch',
      },
      {
        q: 'Can I connect my own domain and edit the site later?',
        a: 'Yes. Most builders support custom domains and allow ongoing edits after publishing.\n\n• Connect a domain and enable SSL\n• Update pages anytime in the editor\n• Publish changes instantly',
      },
      {
        q: 'Which AI website builder is best for beginners?',
        a: 'The best option depends on how easy the editor feels and how good the default results are for your needs.\n\n• Simple onboarding and clear editor\n• Good design presets and flexibility\n• SEO settings that are easy to control\n• See our Rating section for beginner-friendly picks',
      },
      {
        q: "AI builder vs hiring a web studio: what's the difference?",
        a: 'AI builders are faster and cheaper, while studios offer deeper strategy and custom work.\n\n• AI builder: launch in minutes, self-service editing, lower cost\n• Web studio: custom design, branding, advanced features, support\n• Use the comparison table above to choose the best fit',
      },
    ],
  },
  trust: {
    editorial: {
      title: 'Editorial Policy',
      desc: 'Independent expert reviews based on real testing.',
    },
    affiliate: {
      title: 'Affiliate Disclosure',
      desc: 'We may earn a commission if you buy via our links, at no extra cost to you.',
    },
    noPaid: {
      title: 'No Paid Placements',
      desc: 'We do not accept money for higher rankings.',
    },
  },
  cta: {
    title: 'Try Building a Website with AI Right Now',
    subtitle:
      'Pick a builder from our rating and launch your site in minutes. It is free to start and requires no skills.',
    button: 'Build a Site with AI',
    badge: 'Free to Start',
    benefits: ['No coding needed', 'Ready in 5 minutes', 'Free to start'],
    socialProof: '',
  },
  newsletter: {
    title: 'Stay Updated',
    subtitle:
      'Get the latest deals and useful articles about AI website builders',
    placeholder: 'Enter your email',
    button: 'Subscribe',
    consent: 'I agree to the processing of personal data',
    consentRequired: 'Please accept the consent to subscribe',
    successMessage:
      'You have successfully subscribed! Check your email for confirmation.',
    privacy: 'Unsubscribe anytime.',
    success: 'Subscribed!',
  },
  blog: {
    title: 'Latest Insights',
    subtitle: 'News and articles about web development with AI',
    readMore: 'Read More',
  },
}

// ═══════════════════════════════════════════════════════
// RUSSIAN
// ═══════════════════════════════════════════════════════
const ru: typeof en = {
  brand: 'Magic AI Builders',
  seo: {
    title: 'Лучшие конструкторы сайтов с ИИ — рейтинг, обзоры и сравнение',
    description:
      'Подборка и сравнение конструкторов сайтов с ИИ: честные обзоры, функции, цены, плюсы и минусы. Выберите лучший вариант под вашу задачу.',
  },
  nav: {
    rating: 'Рейтинг',
    comparison: 'Сравнение',
    howItWorks: 'Как это работает',
    faq: 'Вопросы и ответы',
    start: 'Обзор сервисов',
  },
  hero: {
    h1: 'Лучшие конструкторы сайтов с ИИ: рейтинг и сравнение',
    subtitle:
      'Сравниваем конструкторы сайтов с ИИ: где проще сделать сайт, у кого лучше результат и что подходит именно под вашу задачу. Коротко, по делу, на основе тестов.',
    description1:
      'Мы тестируем конструкторы сайтов с ИИ на практике и оцениваем их по удобству, качеству дизайна, SEO-инструментам, цене и скорости публикации.',
    description2: '',
    cta: 'Смотреть рейтинг',
    compare: 'Сравнить сервисы',
  },
  rankingCovers: { title: '', description: '', bullets: [] },
  whatIsAi: {
    title: 'Почему люди создают сайты с ИИ',
    subtitle:
      'Конструкторы сайтов с ИИ стали популярны, потому что они быстрые, простые и доступны каждому.',
    structure: {
      title: 'Готово за минуты',
      desc: 'ИИ создаёт первый черновик по короткому запросу — без недель ожидания.',
    },
    content: {
      title: 'Дешевле, чем студия',
      desc: 'Доступный старт — стоимость зависит от тарифа.',
    },
    edit: {
      title: 'Просто и понятно',
      desc: 'Редактируйте визуально в простом no-code редакторе.',
    },
    hosting: {
      title: 'Для большинства задач',
      desc: 'Сайты услуг, портфолио, бизнес-страницы, многостраничные сайты — ИИ закрывает самые частые сценарии.',
    },
  },
  howItWorks: {
    title: 'Как ИИ создаёт ваш сайт',
    subtitle:
      'ИИ собирает структуру, тексты и дизайн за минуты — вам остаётся уточнить детали и опубликовать сайт.',
    steps: [
      {
        title: 'Опишите, какой сайт вам нужен',
        desc: 'Укажите сферу, аудиторию и цели сайта.',
      },
      {
        title: 'ИИ создаёт готовый сайт',
        desc: 'Конструктор формирует дизайн, тексты и структуру за минуты.',
      },
      {
        title: 'Сделайте сайт своим',
        desc: 'Редактируйте текст, изображения и стиль в визуальном редакторе.',
      },
      {
        title: 'Подключите домен и запускайте',
        desc: 'Привяжите адрес сайта — и он сразу станет доступен.',
      },
    ],
  },
  topServices: {
    title: 'Рейтинг лучших конструкторов сайтов с ИИ',
    subtitle:
      'Редакционный рейтинг по единой методологии: мы тестируем сервисы на практике и оцениваем результаты.',
    footerText:
      'Мы создаём тестовый сайт в каждом конструкторе и сравниваем удобство, качество результата и возможности редактирования. Также оцениваем SEO-настройки, интеграции и соотношение цены и качества. Итоговая оценка — редакционная, на основе одинаковых критериев.',
    ratingDisclaimer:
      'Редакционная оценка на основе функций, удобства и ценности. Не являются отзывами пользователей.',
    visitSite: 'Попробовать бесплатно',
    labels: {
      bestFor: 'Лучше всего для:',
      pros: 'Сильные стороны:',
      cons: 'Минусы:',
      details: 'Подробнее',
      hide: 'Скрыть',
      seo: 'SEO-оптимизация',
      flexibility: 'Гибкость',
      integrations: 'Интеграции',
      speed: 'Скорость создания',
      difficulty: 'Сложность создания',
    },
    services: svc(
      ['Универсал', 'Дизайн', 'Скорость', 'Цена/Качество', 'WordPress'],
      [
        'Универсальный бизнес',
        'Портфолио и лендинги',
        'Локальные услуги',
        'Бюджетный старт',
        'Сайты на WordPress',
      ],
      [
        ['Высокая гибкость', 'Сильное SEO', 'Много интеграций'],
        ['Максимальная гибкость', 'Хорошее SEO', 'Средние интеграции'],
        ['Быстрая генерация', 'Базовое SEO', 'Базовые интеграции'],
        ['Средняя гибкость', 'Среднее SEO', 'Средние интеграции'],
        ['Высокая гибкость', 'Сильное SEO', 'Много интеграций'],
      ],
      [
        'Может быть сложен для простых задач',
        'Нужно время на освоение',
        'Мало настроек дизайна',
        'Шаблоны могут выглядеть просто',
        'Нужны знания WP для глубоких правок',
      ],
      ['10–30 мин', '10–40 мин', '5–15 мин', '10–30 мин', '30–90 мин'],
      ['Сильная', 'Хорошая', 'Базовая', 'Средняя', 'Сильная'],
      ['Высокая', 'Максимальная', 'Низкая', 'Средняя', 'Высокая'],
      ['Много', 'Средне', 'Базовые', 'Средне', 'Много'],
      ['Легко', 'Сложно', 'Легко', 'Легко', 'Средне'],
    ),
  },
  servicesTable: {
    title: 'Сравнение конструкторов сайтов с ИИ',
    subtitle: 'Редакционный рейтинг по единой методике.',
    footerText:
      'Мы создаём тестовый сайт в каждом конструкторе и сравниваем удобство, качество результата и возможности редактирования.',
    service: 'Сервис',
    type: 'Тип сайтов',
    speed: 'Скорость',
    seo: 'SEO',
    flexibility: 'Гибкость',
    integrations: 'Интеграции',
    price: 'Цена',
  },
  comparison: {
    title: 'Конструктор сайтов с ИИ vs веб-студия: что выбрать',
    subtitle:
      'Фактическое сравнение по срокам, стоимости, правкам и контролю над результатом.',
    intro: '',
    feature: 'Параметр',
    studio: 'Веб-студия',
    ai: 'Конструктор сайтов с ИИ',
    rows: {
      time: { name: 'Срок запуска', studio: '2–8 недель', ai: '5–30 минут' },
      cost: {
        name: 'Типичная стоимость',
        studio: '$2,000–$10,000+',
        ai: '$0–$30/мес',
      },
      revisions: {
        name: 'Правки',
        studio: 'Платные или ограничены',
        ai: 'Безлимитные, мгновенно',
      },
      maintenance: {
        name: 'Поддержка',
        studio: 'Отдельный договор',
        ai: 'Обычно включена',
      },
      control: {
        name: 'Контроль контента',
        studio: 'Через разработчика',
        ai: 'Полное самообслуживание',
      },
    },
  },
  methodology: {
    label: 'Доверие и прозрачность',
    title: 'Как мы оцениваем конструкторы сайтов с ИИ',
    description:
      'Мы не переписываем маркетинговые обещания. Мы действительно тестируем каждый конструктор сайтов с ИИ: создаём сайты, проверяем удобство и сравниваем результаты бок о бок.',
    criteria: [],
    disclaimer: '',
    cards: [
      {
        title: 'Практическое тестирование',
        desc: 'Мы создаём реальные сайты и сравниваем результаты на каждой платформе',
      },
      {
        title: 'Единые критерии',
        desc: 'Каждый конструктор сайтов с ИИ оценивается по одному и тому же набору факторов',
      },
      {
        title: 'Плюсы и минусы — без воды',
        desc: 'Честные оценки с понятными сильными и слабыми сторонами',
      },
      {
        title: 'Непредвзятый рейтинг',
        desc: 'Рейтинг отражает качество, а не спонсорство',
      },
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы о конструкторах сайтов с ИИ',
    subtitle: 'Всё, что нужно знать о создании сайтов с ИИ',
    items: [
      {
        q: 'Что такое конструктор сайтов с ИИ?',
        a: 'Конструктор сайтов с ИИ создаёт черновик сайта по короткому запросу. Он генерирует структуру, базовый текст и дизайн, после чего вы настраиваете детали и публикуете сайт.\n\n• Создаёт страницы: «Главная», «О нас», «Услуги», «Контакты»\n• Визуальный редактор без кода для правок\n• Быстрее, чем ручная разработка\n• Подходит для быстрого запуска и тестирования идей',
      },
      {
        q: 'Как конструктор с ИИ создаёт сайт?',
        a: 'Обычно процесс одинаковый: вы описываете проект, ИИ генерирует черновик, а затем вы дорабатываете его перед публикацией.\n\n• Указываете нишу, цель и предпочтения по стилю\n• ИИ создаёт макет, блоки и текст\n• Редактируете секции, шрифты, изображения и меню\n• Подключаете домен и публикуете',
      },
      {
        q: 'Нужны ли навыки программирования или дизайна?',
        a: 'Нет. Конструкторы с ИИ рассчитаны на новичков и работают через визуальный редактор без кода.\n\n• Правите текст и блоки как в обычном конструкторе\n• Настраиваете стиль без программирования\n• Чем точнее ввод (цель, аудитория, тон), тем лучше результат',
      },
      {
        q: 'Подходят ли сайты, сделанные с ИИ, для SEO?',
        a: 'Да — если доработать контент и базовые SEO-настройки. ИИ ускоряет старт, но позиции зависят от качества и структуры.\n\n• Уникальные заголовки страниц (Title) и мета-описания\n• Понятная структура H1/H2 и внутренняя перелинковка\n• Полезный контент без дублей\n• Быстрая загрузка и удобство на мобильных',
      },
      {
        q: 'Можно ли создать многостраничный сайт с ИИ?',
        a: 'Да. Большинство конструкторов с ИИ создают многостраничные сайты с меню и типовыми страницами.\n\n• «Главная», «О нас», «Услуги», «Контакты», «Блог» (зависит от сервиса)\n• Меню и внутренние ссылки\n• Простое редактирование и обновления после запуска',
      },
      {
        q: 'Можно ли подключить свой домен и редактировать сайт позже?',
        a: 'Да. Большинство конструкторов поддерживают собственный домен и позволяют вносить правки после публикации.\n\n• Подключаете домен и включаете SSL\n• Обновляете страницы в любое время в редакторе\n• Публикуете изменения мгновенно',
      },
      {
        q: 'Какой конструктор с ИИ лучше для новичков?',
        a: 'Лучший вариант зависит от того, насколько удобен редактор и насколько качественный результат «по умолчанию» для ваших задач.\n\n• Понятный старт и удобный редактор\n• Хорошие шаблоны дизайна и гибкость\n• SEO-настройки, которыми легко управлять\n• Смотрите наш раздел «Рейтинг» — там есть варианты для новичков',
      },
      {
        q: 'Конструктор с ИИ или веб-студия: в чём разница?',
        a: 'Конструкторы с ИИ быстрее и дешевле, а студии дают более глубокую стратегию и индивидуальную разработку.\n\n• Конструктор с ИИ: запуск за минуты, самостоятельное редактирование, ниже стоимость\n• Веб-студия: уникальный дизайн, бренд-стиль, сложные функции, поддержка\n• Сравните по таблице выше и выберите подходящий вариант',
      },
    ],
  },
  trust: {
    editorial: {
      title: 'Редакционная политика',
      desc: 'Независимые экспертные обзоры на основе реального тестирования.',
    },
    affiliate: {
      title: 'Партнерские ссылки',
      desc: 'Мы можем получать комиссию при покупке по ссылкам, это не влияет на цену для вас.',
    },
    noPaid: {
      title: 'Без платных размещений',
      desc: 'Мы не принимаем деньги за повышение позиций в рейтинге.',
    },
  },
  cta: {
    title: 'Попробуйте создать сайт с ИИ прямо сейчас',
    subtitle:
      'Выберите конструктор из нашего рейтинга и запустите свой сайт за несколько минут. Это бесплатно и не требует навыков.',
    button: 'Создать сайт с ИИ',
    badge: 'Бесплатный старт',
    benefits: ['Без программирования', 'Готово за 5 минут', 'Бесплатный старт'],
    socialProof: '',
  },
  newsletter: {
    title: 'Будьте в курсе',
    subtitle:
      'Получайте лучшие предложения и полезные статьи о конструкторах сайтов с ИИ',
    placeholder: 'Введите email',
    button: 'Подписаться',
    consent: 'Я согласен на обработку персональных данных',
    consentRequired: 'Необходимо дать согласие для подписки',
    successMessage:
      'Вы успешно подписались! Проверьте почту для подтверждения.',
    privacy: 'Отписаться можно в любой момент.',
    success: 'Вы подписаны!',
  },
  blog: {
    title: 'Последние статьи',
    subtitle: 'Новости и материалы о веб-разработке с ИИ',
    readMore: 'Читать далее',
  },
}

// ═══════════════════════════════════════════════════════
// UKRAINIAN
// ═══════════════════════════════════════════════════════
const ua: typeof en = {
  brand: 'Magic AI Builders',
  seo: {
    title: 'Найкращі конструктори сайтів зі ШІ — рейтинг, огляди та порівняння',
    description:
      'Добірка та порівняння конструкторів сайтів зі ШІ: чесні огляди, функції, ціни, плюси й мінуси. Оберіть найкращий варіант під вашу задачу.',
  },
  nav: {
    rating: 'Рейтинг',
    comparison: 'Порівняння',
    howItWorks: 'Як це працює',
    faq: 'Питання та відповіді',
    start: 'Огляд сервісів',
  },
  hero: {
    h1: 'Найкращі конструктори сайтів зі ШІ: рейтинг і порівняння',
    subtitle:
      'Порівнюємо конструктори сайтів зі ШІ: де простіше зробити сайт, у кого кращий результат і що підходить саме для вашого завдання. Коротко, по суті, на основі тестів.',
    description1:
      'Ми тестуємо конструктори сайтів зі ШІ на практиці та оцінюємо їх за зручністю, якістю дизайну, SEO-інструментами, ціною та швидкістю публікації.',
    description2: '',
    cta: 'Переглянути рейтинг',
    compare: 'Порівняти сервіси',
  },
  rankingCovers: { title: '', description: '', bullets: [] },
  whatIsAi: {
    title: 'Чому люди створюють сайти зі ШІ',
    subtitle:
      'Конструктори сайтів зі ШІ стали популярними, бо вони швидкі, прості й доступні кожному.',
    structure: {
      title: 'Готово за хвилини',
      desc: 'ШІ створює перший чернетковий варіант з короткого запиту — без тижнів очікування.',
    },
    content: {
      title: 'Дешевше, ніж студія',
      desc: 'Доступний старт — вартість залежить від тарифу.',
    },
    edit: {
      title: 'Просто й зрозуміло',
      desc: 'Редагуйте візуально в простому no-code редакторі.',
    },
    hosting: {
      title: 'Для більшості завдань',
      desc: 'Сайти послуг, портфоліо, бізнес-сторінки, багатосторінкові сайти — ШІ покриває найпоширеніші сценарії.',
    },
  },
  howItWorks: {
    title: 'Як ШІ створює ваш сайт',
    subtitle:
      'ШІ збирає структуру, тексти та дизайн за хвилини — ви лише уточнюєте деталі та публікуєте сайт.',
    steps: [
      {
        title: 'Опишіть, який сайт вам потрібен',
        desc: 'Вкажіть тип бізнесу, цільову аудиторію та завдання сайту',
      },
      {
        title: 'ШІ згенерує готовий сайт',
        desc: 'Конструктор створить дизайн, тексти та структуру за кілька хвилин',
      },
      {
        title: 'Внесіть правки під себе',
        desc: 'Змініть тексти, зображення та стиль через візуальний редактор',
      },
      {
        title: 'Підключіть домен та запускайте',
        desc: "Прив'яжіть адресу сайту і він одразу доступний в інтернеті",
      },
    ],
  },
  topServices: {
    title: 'Рейтинг найкращих конструкторів сайтів зі ШІ',
    subtitle:
      'Редакційний рейтинг за єдиною методологією: ми тестуємо сервіси на практиці та оцінюємо результати.',
    footerText:
      'Ми створюємо тестовий сайт у кожному конструкторі та порівнюємо зручність, якість результату й можливості редагування. Також оцінюємо SEO-налаштування, інтеграції та співвідношення ціни й цінності. Підсумкова оцінка — редакційна, на основі однакових критеріїв.',
    ratingDisclaimer:
      'Редакційна оцінка на основі функцій, зручності та цінності. Не є відгуками користувачів.',
    visitSite: 'Спробувати безкоштовно',
    labels: {
      bestFor: 'Найкраще для:',
      pros: 'Переваги:',
      cons: 'Недоліки:',
      details: 'Детальніше',
      hide: 'Приховати',
      seo: 'SEO-оптимізація',
      flexibility: 'Гнучкість',
      integrations: 'Інтеграції',
      speed: 'Швидкість створення',
      difficulty: 'Складність створення',
    },
    services: svc(
      ['Універсал', 'Дизайн', 'Швидкість', 'Ціна/Якість', 'WordPress'],
      [
        'Універсальний бізнес',
        'Портфоліо та лендинги',
        'Локальні послуги',
        'Бюджетний старт',
        'Сайти на WordPress',
      ],
      [
        ['Висока гнучкість', 'Потужне SEO', 'Багато інтеграцій'],
        ['Максимальна гнучкість', 'Гарне SEO', 'Середні інтеграції'],
        ['Швидка генерація', 'Базове SEO', 'Базові інтеграції'],
        ['Середня гнучкість', 'Середнє SEO', 'Середні інтеграції'],
        ['Висока гнучкість', 'Потужне SEO', 'Багато інтеграцій'],
      ],
      [
        'Може бути складним для простих завдань',
        'Потрібен час на освоєння',
        'Мало налаштувань дизайну',
        'Шаблони можуть виглядати просто',
        'Потрібні знання WP для глибоких правок',
      ],
      ['10–30 хв', '10–40 хв', '5–15 хв', '10–30 хв', '30–90 хв'],
      ['Сильна', 'Хороша', 'Базова', 'Середня', 'Сильна'],
      ['Висока', 'Максимальна', 'Низька', 'Середня', 'Висока'],
      ['Багато', 'Середньо', 'Базові', 'Середньо', 'Багато'],
      ['Легко', 'Складно', 'Легко', 'Легко', 'Середньо'],
    ),
  },
  servicesTable: {
    title: 'Порівняння конструкторів сайтів зі ШІ',
    subtitle: 'Редакційний рейтинг за єдиною методикою.',
    footerText:
      'Ми створюємо тестовий сайт у кожному конструкторі та порівнюємо зручність, якість результату та можливості редагування.',
    service: 'Сервіс',
    type: 'Тип сайтів',
    speed: 'Швидкість',
    seo: 'SEO',
    flexibility: 'Гнучкість',
    integrations: 'Інтеграції',
    price: 'Ціна',
  },
  comparison: {
    title: 'Конструктор зі ШІ чи веб-студія: що обрати',
    subtitle:
      'Фактичне порівняння за термінами, ціною, правками та контролем результату.',
    intro: '',
    feature: 'Характеристика',
    studio: 'Веб-студія',
    ai: 'Конструктор сайтів зі ШІ',
    rows: {
      time: {
        name: 'Терміни запуску',
        studio: '2–8 тижнів',
        ai: '5–30 хвилин',
      },
      cost: {
        name: 'Вартість',
        studio: '$2 000 – $10 000+',
        ai: '$0 – $30/міс',
      },
      revisions: {
        name: 'Правки',
        studio: 'Платно або обмежено',
        ai: 'Безлімітно, миттєво',
      },
      maintenance: {
        name: 'Підтримка',
        studio: 'Окремий договір',
        ai: 'Зазвичай включена',
      },
      control: {
        name: 'Контроль контенту',
        studio: 'Через розробника',
        ai: 'Самостійно',
      },
    },
  },
  methodology: {
    label: 'Довіра та прозорість',
    title: 'Як ми оцінюємо конструктори сайтів зі ШІ',
    description:
      'Ми не переписуємо маркетингові обіцянки. Ми справді тестуємо кожен конструктор сайтів зі ШІ: створюємо сайти, перевіряємо зручність і порівнюємо результати поруч.',
    criteria: [],
    disclaimer: '',
    cards: [
      {
        title: 'Практичне тестування',
        desc: 'Ми створюємо реальні сайти та порівнюємо результат на кожній платформі',
      },
      {
        title: 'Єдині критерії',
        desc: 'Кожен конструктор сайтів зі ШІ оцінюється за одним і тим самим набором факторів',
      },
      {
        title: 'Плюси й мінуси — без «води»',
        desc: 'Чесні оцінки з чіткими перевагами та недоліками',
      },
      {
        title: 'Неупереджений рейтинг',
        desc: 'Рейтинг відображає якість, а не спонсорство',
      },
    ],
  },
  faq: {
    title: 'Часті запитання про конструктори сайтів зі ШІ',
    subtitle: 'Усе, що потрібно знати про створення сайтів зі ШІ',
    items: [
      {
        q: 'Що таке конструктор сайтів зі ШІ?',
        a: 'Конструктор сайтів зі ШІ створює чернетку сайту з короткого запиту. Він генерує структуру, базові тексти та дизайн, а потім ви налаштовуєте й публікуєте сайт.\n\n• Створює сторінки: «Головна», «Про нас», «Послуги», «Контакти»\n• Візуальний редактор без коду для змін\n• Швидший старт, ніж ручна розробка\n• Підходить для швидких запусків і тестування ідей',
      },
      {
        q: 'Як конструктор зі ШІ створює сайт?',
        a: 'Зазвичай процес однаковий: ви описуєте проєкт, ШІ генерує чернетку, а ви доопрацьовуєте її перед публікацією.\n\n• Вказуєте нішу, цілі та вподобання щодо стилю\n• ШІ генерує макет, секції й текст\n• Редагуєте блоки, шрифти, зображення та меню\n• Підключаєте домен і публікуєте',
      },
      {
        q: 'Чи потрібні навички коду або дизайну?',
        a: 'Ні. Конструктори зі ШІ створені для новачків і працюють через візуальний редактор без коду.\n\n• Редагуєте текст і секції як у звичайному конструкторі\n• Налаштовуєте стиль без програмування\n• Чим чіткіший запит (ціль, аудиторія, тон), тим кращий результат',
      },
      {
        q: 'Чи підходять сайти, зроблені зі ШІ, для SEO?',
        a: 'Так — якщо допрацювати контент і базові SEO-налаштування. ШІ пришвидшує старт, але позиції залежать від якості та структури.\n\n• Унікальні Title і meta description\n• Чітка структура H1/H2 та внутрішні посилання\n• Корисний контент без дублів\n• Швидке завантаження й зручність на мобільних',
      },
      {
        q: 'Чи можна зробити багатосторінковий сайт зі ШІ?',
        a: 'Так. Більшість конструкторів зі ШІ генерують багатосторінкові сайти з меню та стандартними сторінками.\n\n• «Головна», «Про нас», «Послуги», «Контакти», «Блог» (залежно від сервісу)\n• Меню та внутрішні посилання\n• Легке редагування й оновлення після запуску',
      },
      {
        q: 'Чи можна підключити власний домен і редагувати сайт пізніше?',
        a: 'Так. Більшість конструкторів підтримують власні домени й дозволяють постійні зміни після публікації.\n\n• Підключаєте домен і включаєте SSL\n• Оновлюєте сторінки в будь-який час в редакторі\n• Публікуєте зміни миттєво',
      },
      {
        q: 'Який конструктор зі ШІ найкращий для новачків?',
        a: 'Найкращий варіант залежить від зручності редактора та якості результату за замовчуванням під ваші задачі.\n\n• Простий старт і зрозумілий редактор\n• Хороші дизайн-пресети та гнучкість\n• SEO-налаштування, якими легко керувати\n• Дивіться наш розділ «Рейтинг» — там є добірки для новачків',
      },
      {
        q: 'Конструктор зі ШІ чи веб-студія: у чому різниця?',
        a: 'Конструктори зі ШІ швидші й дешевші, а студії дають глибшу стратегію та індивідуальну розробку.\n\n• Конструктор зі ШІ: запуск за хвилини, самостійне редагування, нижча ціна\n• Веб-студія: індивідуальний дизайн, брендинг, складні функції, підтримка\n• Скористайтеся таблицею вище, щоб обрати найкращий варіант',
      },
    ],
  },
  trust: {
    editorial: {
      title: 'Редакційна політика',
      desc: 'Незалежні експертні огляди на основі реального тестування.',
    },
    affiliate: {
      title: 'Партнерські посилання',
      desc: 'Ми можемо отримувати комісію при покупці за посиланнями, без додаткових витрат для вас.',
    },
    noPaid: {
      title: 'Без платних розміщень',
      desc: 'Ми не приймаємо гроші за підвищення позицій у рейтингу.',
    },
  },
  cta: {
    title: 'Спробуйте створити сайт зі ШІ прямо зараз',
    subtitle:
      'Оберіть конструктор з нашого рейтингу та запустіть свій сайт за кілька хвилин. Це безкоштовно і не потребує навичок.',
    button: 'Створити сайт зі ШІ',
    badge: 'Безкоштовний старт',
    benefits: ['Без програмування', 'Готово за 5 хвилин', 'Безкоштовний старт'],
    socialProof: '',
  },
  newsletter: {
    title: 'Будьте в курсі',
    subtitle:
      'Отримуйте найкращі пропозиції та корисні статті про конструктори сайтів зі ШІ',
    placeholder: 'Введіть email',
    button: 'Підписатися',
    consent: 'Я погоджуюсь на обробку персональних даних',
    consentRequired: 'Необхідно дати згоду для підписки',
    successMessage:
      'Ви успішно підписалися! Перевірте пошту для підтвердження.',
    privacy: 'Відписатися можна в будь-який момент.',
    success: 'Ви підписані!',
  },
  blog: {
    title: 'Останні статті',
    subtitle: 'Новини та матеріали про веб-розробку зі ШІ',
    readMore: 'Читати далі',
  },
}

// ═══════════════════════════════════════════════════════
// Helper to build a language object from partial overrides
// ═══════════════════════════════════════════════════════
function makeLang(
  overrides: Partial<typeof en> & {
    seo: typeof en.seo
    nav: typeof en.nav
    hero: typeof en.hero
    faq: typeof en.faq
  },
): typeof en {
  return {
    brand: 'Magic AI Builders',
    rankingCovers: { title: '', description: '', bullets: [] },
    ...en,
    ...overrides,
  }
}

// ═══════════════════════════════════════════════════════
// GERMAN
// ═══════════════════════════════════════════════════════
const de: typeof en = makeLang({
  seo: {
    title: 'Die besten KI-Website-Builder — Vergleich, Tests & Preise',
    description:
      'Entdecke und vergleiche KI-Website-Builder. Unabhängige Tests, Funktionen, Preise sowie Vor- und Nachteile — finde den passenden Builder.',
  },
  nav: {
    rating: 'Bewertung',
    comparison: 'Vergleich',
    howItWorks: "So funktioniert's",
    faq: 'FAQ',
    start: 'Services bewerten',
  },
  hero: {
    h1: 'Die besten Website-Builder mit KI: Bewertung & Vergleich',
    subtitle:
      'Wir vergleichen Website-Builder mit KI: Welcher ist am einfachsten, welcher liefert die besten Ergebnisse und welcher passt zu Ihren Anforderungen.',
    description1:
      'Wir testen Website-Builder mit KI in der Praxis und bewerten sie nach Bedienkomfort, Designqualität, SEO-Tools, Preis und Veröffentlichungsgeschwindigkeit.',
    description2: '',
    cta: 'Bewertung ansehen',
    compare: 'Baukästen vergleichen',
  },
  whatIsAi: {
    title: 'Warum Menschen Websites mit KI erstellen',
    subtitle:
      'Website-Builder mit KI sind beliebt, weil sie schnell, einfach und für alle zugänglich sind.',
    structure: {
      title: 'In Minuten fertig',
      desc: 'KI erstellt den ersten Entwurf aus einem kurzen Prompt — ohne wochenlanges Warten.',
    },
    content: {
      title: 'Günstiger als eine Agentur',
      desc: 'Preiswerter Einstieg — die Kosten hängen vom Tarif ab.',
    },
    edit: {
      title: 'Einfach und klar',
      desc: 'Visuell bearbeiten — mit einem einfachen No-Code-Editor.',
    },
    hosting: {
      title: 'Für die meisten Anforderungen',
      desc: 'Service-Websites, Portfolios, Business-Seiten, mehrseitige Websites — KI deckt die häufigsten Fälle ab.',
    },
  },
  howItWorks: {
    title: 'So erstellt KI Ihre Website',
    subtitle:
      'KI setzt Struktur, Texte und Design in wenigen Minuten zusammen — Sie verfeinern nur noch die Details und veröffentlichen.',
    steps: [
      {
        title: 'Beschreiben Sie die Website, die Sie brauchen',
        desc: 'Nennen Sie Branche, Zielgruppe und Ziele Ihrer Website.',
      },
      {
        title: 'KI erstellt eine fertige Website',
        desc: 'Der Builder erzeugt Design, Texte und Struktur in Minuten.',
      },
      {
        title: 'Machen Sie sie zu Ihrer eigenen',
        desc: 'Bearbeiten Sie Text, Bilder und Stil im visuellen Editor.',
      },
      {
        title: 'Domain verbinden & starten',
        desc: 'Webadresse verknüpfen — und Ihre Website ist sofort online.',
      },
    ],
  },
  topServices: {
    ...en.topServices,
    title: 'Bewertung der besten Website-Builder mit KI',
    subtitle:
      'Redaktionelles Ranking nach einheitlicher Methodik: Wir testen die Services praktisch und bewerten die Ergebnisse.',
    footerText:
      'Wir erstellen in jedem Builder eine Test-Website und vergleichen Bedienbarkeit, Ergebnisqualität und Bearbeitungsmöglichkeiten. Außerdem bewerten wir SEO-Einstellungen, Integrationen und das Preis-Leistungs-Verhältnis.',
    ratingDisclaimer:
      'Redaktionelle Bewertung basierend auf Funktionen, Benutzerfreundlichkeit und Preis-Leistung. Keine Nutzerbewertungen.',
    visitSite: 'Kostenlos testen',
    labels: {
      bestFor: 'Am besten für:',
      pros: 'Vorteile:',
      cons: 'Nachteile:',
      details: 'Details anzeigen',
      hide: 'Ausblenden',
      seo: 'SEO-Optimierung',
      flexibility: 'Flexibilität',
      integrations: 'Integrationen',
      speed: 'Erstellungsdauer',
      difficulty: 'Erstellungsaufwand',
    },
    services: svc(
      ['Universal', 'Design', 'Geschwindigkeit', 'Preis-Leistung', 'WordPress'],
      [
        'Universelles Business',
        'Portfolios & Landingpages',
        'Lokale Dienstleistungen',
        'Budgetfreundlicher Start',
        'WordPress-Websites',
      ],
      [
        ['Hohe Flexibilität', 'Starkes SEO', 'Viele Integrationen'],
        ['Maximale Flexibilität', 'Gutes SEO', 'Mittlere Integrationen'],
        ['Schnelle Generierung', 'Basis-SEO', 'Basis-Integrationen'],
        ['Mittlere Flexibilität', 'Mittleres SEO', 'Mittlere Integrationen'],
        ['Hohe Flexibilität', 'Starkes SEO', 'Viele Integrationen'],
      ],
      [
        'Kann für einfache Seiten überwältigend sein',
        'Steilere Lernkurve',
        'Begrenzte Designanpassung',
        'Vorlagen können generisch wirken',
        'WP-Kenntnisse für tiefe Anpassungen nötig',
      ],
      ['10–30 Min.', '10–40 Min.', '5–15 Min.', '10–30 Min.', '30–90 Min.'],
      ['Stark', 'Gut', 'Basis', 'Mittel', 'Stark'],
      ['Hoch', 'Maximal', 'Niedrig', 'Mittel', 'Hoch'],
      ['Viele', 'Mittel', 'Basis', 'Mittel', 'Viele'],
      ['Einfach', 'Schwierig', 'Einfach', 'Einfach', 'Mittel'],
    ),
  },
  servicesTable: {
    title: 'Website-Builder mit KI vergleichen',
    subtitle: 'Redaktionelles Ranking nach einheitlicher Methodik.',
    footerText:
      'Wir erstellen eine Test-Website in jedem Baukasten und vergleichen Benutzerfreundlichkeit und Ergebnisqualität.',
    service: 'Dienst',
    type: 'Website-Typ',
    speed: 'Geschwindigkeit',
    seo: 'SEO',
    flexibility: 'Flexibilität',
    integrations: 'Integrationen',
    price: 'Preis',
  },
  comparison: {
    title: 'Website-Builder mit KI vs. Webagentur: Was ist die bessere Wahl?',
    subtitle:
      'Ein sachlicher Vergleich nach Zeitplan, Kosten, Änderungsrunden und Kontrolle über das Ergebnis.',
    intro: '',
    feature: 'Merkmal',
    studio: 'Webagentur',
    ai: 'Website-Builder mit KI',
    rows: {
      time: {
        name: 'Zeit bis zum Launch',
        studio: '2–8 Wochen',
        ai: '5–30 Minuten',
      },
      cost: {
        name: 'Typische Kosten',
        studio: '$2,000–$10,000+',
        ai: '$0–$30/Monat',
      },
      revisions: {
        name: 'Änderungen',
        studio: 'Kostenpflichtig oder begrenzt',
        ai: 'Unbegrenzt, sofort',
      },
      maintenance: {
        name: 'Laufender Support',
        studio: 'Separater Vertrag',
        ai: 'Meist inklusive',
      },
      control: {
        name: 'Inhaltskontrolle',
        studio: 'Über den Entwickler',
        ai: 'Vollständige Selbstverwaltung',
      },
    },
  },
  methodology: {
    label: 'Vertrauen & Transparenz',
    title: 'So bewerten wir Website-Builder mit KI',
    description:
      'Wir schreiben nicht einfach Marketingversprechen um. Wir testen jeden Website-Builder mit KI wirklich: Wir erstellen Websites, prüfen die Bedienbarkeit und vergleichen die Ergebnisse direkt.',
    criteria: [],
    disclaimer: '',
    cards: [
      {
        title: 'Praxisnahe Tests',
        desc: 'Wir bauen echte Websites und vergleichen die Ergebnisse auf jeder Plattform',
      },
      {
        title: 'Einheitliche Kriterien',
        desc: 'Jeder Website-Builder mit KI wird nach demselben Kriterienkatalog bewertet',
      },
      {
        title: 'Pro & Contra, ohne Bla-bla',
        desc: 'Ehrliche Bewertungen mit klaren Stärken und Schwächen',
      },
      {
        title: 'Unvoreingenommene Bewertung',
        desc: 'Rankings spiegeln Qualität wider, nicht Sponsoring',
      },
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen zu Website-Buildern mit KI',
    subtitle:
      'Alles, was Sie über das Erstellen von Websites mit KI wissen müssen',
    items: [
      {
        q: 'Was ist ein Website-Builder mit KI?',
        a: 'Ein Website-Builder mit KI erstellt aus einem kurzen Prompt einen Website-Entwurf. Er erzeugt Struktur, Basistexte und Design — danach passen Sie alles an und veröffentlichen die Seite.\n\n• Erstellt Seiten wie Start, Über uns, Leistungen, Kontakt\n• Visueller No-Code-Editor für Änderungen\n• Schneller als manuelles Erstellen\n• Ideal für schnelle Launches und das Testen von Ideen',
      },
      {
        q: 'Wie erstellt ein Website-Builder mit KI eine Website?',
        a: 'Meist läuft es ähnlich ab: Sie beschreiben Ihr Projekt, die KI erzeugt einen Entwurf, und Sie verfeinern ihn vor der Veröffentlichung.\n\n• Nische, Ziele und Stilvorlieben angeben\n• KI erstellt Layout, Abschnitte und Text\n• Blöcke, Schriften, Bilder und Navigation bearbeiten\n• Domain verbinden und veröffentlichen',
      },
      {
        q: 'Brauche ich Programmier- oder Designkenntnisse?',
        a: 'Nein. Website-Builder mit KI sind für Einsteiger gemacht und funktionieren mit einem visuellen No-Code-Editor.\n\n• Text und Sektionen wie in einem Page-Builder bearbeiten\n• Styles ohne Programmierung anpassen\n• Je klarer Ihre Vorgaben (Ziel, Zielgruppe, Ton), desto besser das Ergebnis',
      },
      {
        q: 'Sind mit KI erstellte Websites gut für SEO?',
        a: 'Ja — wenn Sie Inhalte und grundlegende SEO-Einstellungen optimieren. KI hilft beim Start, Rankings hängen aber von Qualität und Struktur ab.\n\n• Einzigartige Seitentitel und Meta-Descriptions\n• Klare H1/H2-Struktur und interne Verlinkung\n• Hilfreiche, nicht duplizierte Inhalte\n• Schnelle Ladezeiten und mobile Nutzbarkeit',
      },
      {
        q: 'Kann ich mit KI eine mehrseitige Website erstellen?',
        a: 'Ja. Die meisten Website-Builder mit KI können mehrseitige Websites mit Navigation und Standardseiten generieren.\n\n• Start, Über uns, Leistungen, Kontakt, Blog (je nach Builder)\n• Menü und interne Verlinkung\n• Einfaches Bearbeiten und Aktualisieren nach dem Launch',
      },
      {
        q: 'Kann ich meine eigene Domain verbinden und später weiter bearbeiten?',
        a: 'Ja. Die meisten Builder unterstützen eigene Domains und erlauben laufende Änderungen nach der Veröffentlichung.\n\n• Domain verbinden und SSL aktivieren\n• Seiten jederzeit im Editor aktualisieren\n• Änderungen sofort veröffentlichen',
      },
      {
        q: 'Welcher Website-Builder mit KI ist am besten für Einsteiger?',
        a: 'Das hängt davon ab, wie einfach sich der Editor anfühlt und wie gut die Standard-Ergebnisse für Ihre Ziele sind.\n\n• Einfaches Onboarding und klarer Editor\n• Gute Design-Presets und Flexibilität\n• SEO-Einstellungen, die leicht zu steuern sind\n• Sehen Sie in unseren Bereich Rating für einsteigerfreundliche Empfehlungen',
      },
      {
        q: 'Website-Builder mit KI vs. Webagentur: Was ist der Unterschied?',
        a: 'Website-Builder mit KI sind schneller und günstiger, Agenturen bieten mehr Strategie und maßgeschneiderte Umsetzung.\n\n• Website-Builder mit KI: Launch in Minuten, Self-Service-Bearbeitung, geringere Kosten\n• Webagentur: individuelles Design, Branding, Advanced-Funktionen, Support\n• Nutzen Sie die Vergleichstabelle oben, um die beste Option zu wählen',
      },
    ],
  },
  trust: {
    editorial: {
      title: 'Redaktionelle Richtlinie',
      desc: 'Unabhängige Expertenbewertungen basierend auf echten Tests.',
    },
    affiliate: {
      title: 'Affiliate-Hinweis',
      desc: 'Wir erhalten möglicherweise eine Provision bei Käufen über unsere Links.',
    },
    noPaid: {
      title: 'Keine bezahlten Platzierungen',
      desc: 'Wir akzeptieren kein Geld für höhere Rankings.',
    },
  },
  cta: {
    title: 'Erstellen Sie jetzt eine Website mit KI',
    subtitle:
      'Wählen Sie einen Baukasten aus unserer Bewertung und starten Sie Ihre Website in wenigen Minuten.',
    button: 'Website mit KI erstellen',
    badge: 'Kostenloser Start',
    benefits: [
      'Keine Programmierung nötig',
      'In 5 Minuten fertig',
      'Kostenloser Start',
    ],
    socialProof: '',
  },
  newsletter: {
    title: 'Bleiben Sie informiert',
    subtitle:
      'Erhalten Sie die neuesten Angebote und nützliche Artikel über Website-Builder mit KI',
    placeholder: 'E-Mail eingeben',
    button: 'Abonnieren',
    consent: 'Ich stimme der Verarbeitung personenbezogener Daten zu',
    consentRequired: 'Bitte stimmen Sie zu',
    successMessage: 'Erfolgreich abonniert!',
    privacy: 'Jederzeit abbestellbar.',
    success: 'Abonniert!',
  },
  blog: {
    title: 'Neueste Einblicke',
    subtitle: 'Nachrichten und Artikel über Webentwicklung mit KI',
    readMore: 'Weiterlesen',
  },
})

// ═══════════════════════════════════════════════════════
// SPANISH
// ═══════════════════════════════════════════════════════
const es: typeof en = makeLang({
  seo: {
    title: 'Los mejores creadores web con IA — comparativa, reseñas y precios',
    description:
      'Descubre y compara creadores de sitios web con IA. Reseñas independientes, funciones, precios y pros/contras para elegir el mejor para tu web.',
  },
  nav: {
    rating: 'Ranking',
    comparison: 'Comparación',
    howItWorks: 'Cómo funciona',
    faq: 'Preguntas frecuentes',
    start: 'Reseñar servicios',
  },
  hero: {
    h1: 'Los mejores creadores de sitios web con IA: ranking y comparación',
    subtitle:
      'Comparamos constructores web con IA: cuál es más fácil de usar, cuál da mejores resultados y cuál se adapta a tus necesidades.',
    description1:
      'Probamos creadores de sitios web con IA en la práctica y los evaluamos por facilidad de uso, calidad del diseño, herramientas SEO, precio y velocidad de publicación.',
    description2: '',
    cta: 'Ver ranking',
    compare: 'Comparar servicios',
  },
  whatIsAi: {
    title: 'Por qué la gente crea sitios web con IA',
    subtitle:
      'Los creadores de sitios web con IA se hicieron populares porque son rápidos, sencillos y accesibles para cualquiera.',
    structure: {
      title: 'Listo en minutos',
      desc: 'La IA genera un primer borrador a partir de un prompt corto — sin semanas de espera.',
    },
    content: {
      title: 'Más barato que un estudio',
      desc: 'Fácil para empezar — el precio depende del plan.',
    },
    edit: {
      title: 'Simple y claro',
      desc: 'Edita de forma visual con un editor no-code sencillo.',
    },
    hosting: {
      title: 'Para la mayoría de necesidades',
      desc: 'Sitios de servicios, portafolios, páginas de negocio, webs multipágina — la IA cubre los casos más comunes.',
    },
  },
  howItWorks: {
    title: 'Cómo la IA crea tu sitio web',
    subtitle:
      'La IA arma la estructura, los textos y el diseño en minutos — tú solo ajustas detalles y publicas.',
    steps: [
      {
        title: 'Describe el sitio que necesitas',
        desc: 'Indica tu tipo de negocio, público objetivo y objetivos del sitio.',
      },
      {
        title: 'La IA genera un sitio listo',
        desc: 'El creador produce diseño, textos y estructura en minutos.',
      },
      {
        title: 'Hazlo tuyo',
        desc: 'Edita textos, imágenes y estilo en un editor visual.',
      },
      {
        title: 'Conecta el dominio y publica',
        desc: 'Vincula tu dirección web y el sitio queda online al instante.',
      },
    ],
  },
  topServices: {
    ...en.topServices,
    title: 'Ranking de los mejores creadores de sitios web con IA',
    subtitle:
      'Ranking editorial con una metodología unificada: probamos los servicios en la práctica y evaluamos los resultados.',
    footerText:
      'Creamos un sitio de prueba en cada creador y comparamos la usabilidad, la calidad del resultado y las opciones de edición.',
    ratingDisclaimer:
      'Puntuación editorial basada en funciones, usabilidad y valor. No son reseñas de usuarios.',
    visitSite: 'Probar gratis',
    labels: {
      bestFor: 'Ideal para:',
      pros: 'Ventajas:',
      cons: 'Desventajas:',
      details: 'Ver detalles',
      hide: 'Ocultar detalles',
      seo: 'Optimización SEO',
      flexibility: 'Flexibilidad',
      integrations: 'Integraciones',
      speed: 'Tiempo de creación',
      difficulty: 'Dificultad de creación',
    },
    services: svc(
      ['Universal', 'Diseño', 'Velocidad', 'Precio/Calidad', 'WordPress'],
      [
        'Negocios en general',
        'Portafolios y landing pages',
        'Servicios locales',
        'Inicio económico',
        'Sitios en WordPress',
      ],
      [
        ['Alta flexibilidad', 'SEO potente', 'Muchas integraciones'],
        ['Máxima flexibilidad', 'Buen SEO', 'Integraciones medias'],
        ['Generación rápida', 'SEO básico', 'Integraciones básicas'],
        ['Flexibilidad media', 'SEO medio', 'Integraciones medias'],
        ['Alta flexibilidad', 'SEO potente', 'Muchas integraciones'],
      ],
      [
        'Puede resultar complejo para sitios simples',
        'Curva de aprendizaje más pronunciada',
        'Personalización de diseño limitada',
        'Las plantillas pueden parecer genéricas',
        'Requiere conocimientos de WP para ediciones profundas',
      ],
      ['10–30 min', '10–40 min', '5–15 min', '10–30 min', '30–90 min'],
      ['Potente', 'Bueno', 'Básico', 'Medio', 'Potente'],
      ['Alta', 'Máxima', 'Baja', 'Media', 'Alta'],
      ['Muchas', 'Medias', 'Básicas', 'Medias', 'Muchas'],
      ['Fácil', 'Difícil', 'Fácil', 'Fácil', 'Media'],
    ),
  },
  servicesTable: {
    title: 'Comparación de creadores de sitios web con IA',
    subtitle: 'Calificación editorial con metodología unificada.',
    footerText:
      'Creamos un sitio de prueba en cada creador y comparamos la usabilidad, la calidad del resultado y las opciones de edición.',
    service: 'Servicio',
    type: 'Tipo de sitio',
    speed: 'Velocidad',
    seo: 'SEO',
    flexibility: 'Flexibilidad',
    integrations: 'Integraciones',
    price: 'Precio',
  },
  comparison: {
    title: 'Creador con IA o estudio web: ¿qué elegir?',
    subtitle:
      'Comparación factual por plazos, coste, revisiones y control del resultado.',
    intro: '',
    feature: 'Característica',
    studio: 'Estudio web',
    ai: 'Creador con IA',
    rows: {
      time: {
        name: 'Plazo de lanzamiento',
        studio: '2–8 semanas',
        ai: '5–30 minutos',
      },
      cost: {
        name: 'Coste típico',
        studio: '$2.000 – $10.000+',
        ai: '$0 – $30/mes',
      },
      revisions: {
        name: 'Revisiones',
        studio: 'De pago o limitadas',
        ai: 'Ilimitadas, al instante',
      },
      maintenance: {
        name: 'Soporte continuo',
        studio: 'Contrato aparte',
        ai: 'Normalmente incluido',
      },
      control: {
        name: 'Control del contenido',
        studio: 'A través del desarrollador',
        ai: 'Autoservicio completo',
      },
    },
  },
  methodology: {
    label: 'Confianza y transparencia',
    title: 'Cómo evaluamos los creadores de sitios web con IA',
    description:
      'No nos limitamos a reescribir promesas de marketing. Probamos cada creador con IA de verdad: creamos sitios, comprobamos la facilidad de uso y comparamos resultados lado a lado.',
    criteria: [],
    disclaimer: '',
    cards: [
      {
        title: 'Pruebas prácticas',
        desc: 'Creamos sitios reales y comparamos resultados en cada plataforma',
      },
      {
        title: 'Criterios coherentes',
        desc: 'Cada creador con IA se evalúa con el mismo conjunto de factores',
      },
      {
        title: 'Pros y contras, sin relleno',
        desc: 'Valoraciones honestas con fortalezas y debilidades claras',
      },
      {
        title: 'Ranking imparcial',
        desc: 'Las clasificaciones reflejan calidad, no patrocinios',
      },
    ],
  },
  faq: {
    title: 'Preguntas frecuentes sobre creadores de sitios web con IA',
    subtitle: 'Todo lo que necesitas saber para crear sitios web con IA',
    items: [
      {
        q: '¿Qué es un creador de sitios web con IA?',
        a: 'Un creador de sitios web con IA genera un borrador a partir de un prompt corto. Crea la estructura, textos básicos y diseño; luego lo personalizas y lo publicas.\n\n• Crea páginas como Inicio, Sobre nosotros, Servicios, Contacto\n• Editor visual sin código para cambios\n• Configuración mucho más rápida que hacerlo manualmente\n• Ideal para lanzamientos rápidos y para probar ideas',
      },
      {
        q: '¿Cómo crea un sitio web un creador con IA?',
        a: 'Normalmente el flujo es el mismo: describes tu proyecto, la IA genera un borrador y tú lo ajustas antes de publicar.\n\n• Indica nicho, objetivos y preferencias de estilo\n• La IA genera el layout, secciones y textos\n• Editas bloques, tipografías, imágenes y navegación\n• Conectas un dominio y publicas',
      },
      {
        q: '¿Necesito saber programar o diseñar?',
        a: 'No. Estos creadores están pensados para principiantes y funcionan con un editor visual sin código.\n\n• Editas texto y secciones como en un constructor\n• Ajustas estilos sin programar\n• Cuanto más claro sea el input (objetivo, audiencia, tono), mejor el resultado',
      },
      {
        q: '¿Los sitios creados con IA son buenos para SEO?',
        a: 'Sí, si pules el contenido y la configuración SEO básica. La IA acelera el inicio, pero el posicionamiento depende de la calidad y la estructura.\n\n• Títulos de página y meta descripciones únicos\n• Encabezados H1/H2 claros y enlaces internos\n• Contenido útil y no duplicado\n• Carga rápida y buena experiencia móvil',
      },
      {
        q: '¿Puedo crear un sitio multipágina con IA?',
        a: 'Sí. La mayoría puede generar sitios multipágina con menú y páginas estándar.\n\n• Inicio, Sobre, Servicios, Contacto, Blog (según el creador)\n• Menú y enlazado interno\n• Edición y actualizaciones sencillas tras el lanzamiento',
      },
      {
        q: '¿Puedo conectar mi propio dominio y editar después?',
        a: 'Sí. La mayoría permite dominios personalizados y edición continua tras publicar.\n\n• Conectar dominio y activar SSL\n• Actualizar páginas cuando quieras\n• Publicar cambios al instante',
      },
      {
        q: '¿Qué creador con IA es mejor para principiantes?',
        a: 'Depende de lo intuitivo que sea el editor y de la calidad del resultado inicial para tu caso.\n\n• Onboarding simple y editor claro\n• Buenos presets de diseño y flexibilidad\n• SEO fácil de controlar\n• Mira nuestra sección Rating para opciones recomendadas para principiantes',
      },
      {
        q: 'IA vs contratar un estudio web: ¿cuál es la diferencia?',
        a: 'La IA es más rápida y barata; un estudio ofrece estrategia y trabajo a medida.\n\n• Creador con IA: lanzamiento en minutos, edición autogestionada, menor coste\n• Estudio web: diseño personalizado, branding, funciones avanzadas, soporte\n• Usa la tabla comparativa de arriba para elegir la mejor opción',
      },
    ],
  },
  trust: {
    editorial: {
      title: 'Política editorial',
      desc: 'Avisos independientes de expertos basados en pruebas reales.',
    },
    affiliate: {
      title: 'Divulgación de afiliados',
      desc: 'Podemos recibir una comisión si compras a través de nuestros enlaces, sin coste adicional para ti.',
    },
    noPaid: {
      title: 'Sin colocaciones pagadas',
      desc: 'No aceptamos dinero por posiciones más altas en el ranking.',
    },
  },
  cta: {
    title: 'Prueba a crear una web con IA ahora mismo',
    subtitle:
      'Elige un constructor de nuestro ranking y lanza tu sitio en minutos. Es gratis y no requiere habilidades.',
    button: 'Crear web con IA',
    badge: 'Inicio gratuito',
    benefits: ['Sin programación', 'Listo en 5 minutos', 'Inicio gratuito'],
    socialProof: '',
  },
  newsletter: {
    title: 'Mantente al día',
    subtitle:
      'Recibe las últimas ofertas y artículos útiles sobre creadores de sitios web con IA',
    placeholder: 'Introduce tu email',
    button: 'Suscribirse',
    consent: 'Acepto el procesamiento de datos personales',
    consentRequired: 'Por favor, acepta el consentimiento para suscribirte',
    successMessage:
      '¡Te has suscrito con éxito! Revisa tu email para confirmar.',
    privacy: 'Cancela en cualquier momento.',
    success: '¡Suscrito!',
  },
  blog: {
    title: 'Últimos artículos',
    subtitle: 'Noticias y artículos sobre desarrollo web con IA',
    readMore: 'Leer más',
  },
})

// ═══════════════════════════════════════════════════════
// FRENCH
// ═══════════════════════════════════════════════════════
const fr: typeof en = makeLang({
  seo: {
    title: 'Meilleurs créateurs de sites avec IA — comparatif, avis & tarifs',
    description:
      'Découvrez et comparez des créateurs de sites avec IA. Avis indépendants, fonctionnalités, tarifs, avantages/inconvénients pour choisir le bon outil.',
  },
  nav: {
    rating: 'Classement',
    comparison: 'Comparaison',
    howItWorks: 'Comment ça marche',
    faq: 'FAQ',
    start: 'Évaluer les services',
  },
  hero: {
    h1: 'Les meilleurs créateurs de sites web avec IA : classement & comparaison',
    subtitle:
      'Nous comparons les créateurs de sites avec IA : lequel est le plus simple, lequel donne les meilleurs résultats et lequel correspond à vos besoins.',
    description1:
      "Nous testons des créateurs de sites web avec IA en conditions réelles et les évaluons selon la facilité d'utilisation, la qualité du design, les outils SEO, le prix et la vitesse de publication.",
    description2: '',
    cta: 'Voir le classement',
    compare: 'Comparer les services',
  },
  whatIsAi: {
    title: "Pourquoi créer un site avec l'IA",
    subtitle:
      'Les créateurs de sites avec IA sont populaires car ils sont rapides, simples et accessibles à tous.',
    structure: {
      title: 'Prêt en quelques minutes',
      desc: "L'IA génère un premier brouillon à partir d'un prompt court — sans attendre des semaines.",
    },
    content: {
      title: "Moins cher qu'une agence",
      desc: "Abordable pour démarrer — le prix dépend de l'offre.",
    },
    edit: {
      title: 'Simple et clair',
      desc: 'Modifiez visuellement avec un éditeur no-code simple.',
    },
    hosting: {
      title: 'Pour la plupart des besoins',
      desc: "Sites de services, portfolios, pages business, sites multi-pages — l'IA couvre les usages les plus courants.",
    },
  },
  howItWorks: {
    title: "Comment l'IA crée votre site",
    subtitle:
      "L'IA assemble la structure, les textes et le design en quelques minutes — vous peaufinez les détails et publiez.",
    steps: [
      {
        title: 'Décrivez le site dont vous avez besoin',
        desc: 'Précisez votre activité, votre audience et vos objectifs.',
      },
      {
        title: "L'IA génère un site prêt",
        desc: 'Le builder crée le design, les textes et la structure en quelques minutes.',
      },
      {
        title: 'Personnalisez-le',
        desc: 'Modifiez textes, images et style via un éditeur visuel.',
      },
      {
        title: 'Connectez le domaine et publiez',
        desc: 'Reliez votre adresse web et votre site est en ligne immédiatement.',
      },
    ],
  },
  topServices: {
    ...en.topServices,
    title: 'Classement des meilleurs créateurs de sites web avec IA',
    subtitle:
      'Classement éditorial selon une méthodologie unifiée : nous testons les services en conditions réelles et évaluons les résultats.',
    footerText:
      "Nous créons un site test dans chaque constructeur et comparons la convivialité, la qualité du résultat et les possibilités d'édition.",
    ratingDisclaimer:
      "Note éditoriale basée sur les fonctionnalités, la convivialité et le rapport qualité-prix. Ce ne sont pas des avis d'utilisateurs.",
    visitSite: 'Essayer gratuitement',
    labels: {
      bestFor: 'Idéal pour :',
      pros: 'Avantages :',
      cons: 'Inconvénients :',
      details: 'Voir les détails',
      hide: 'Masquer les détails',
      seo: 'Optimisation SEO',
      flexibility: 'Flexibilité',
      integrations: 'Intégrations',
      speed: 'Vitesse de création',
      difficulty: 'Difficulté de création',
    },
    services: svc(
      ['Universel', 'Design', 'Vitesse', 'Rapport qualité-prix', 'WordPress'],
      [
        'Business universel',
        'Portfolios et landing pages',
        'Services locaux',
        'Démarrage économique',
        'Sites WordPress',
      ],
      [
        ['Grande flexibilité', 'SEO puissant', 'Nombreuses intégrations'],
        ['Flexibilité maximale', 'Bon SEO', 'Intégrations moyennes'],
        ['Génération rapide', 'SEO basique', 'Intégrations basiques'],
        ['Flexibilité moyenne', 'SEO moyen', 'Intégrations moyennes'],
        ['Grande flexibilité', 'SEO puissant', 'Nombreuses intégrations'],
      ],
      [
        'Peut être complexe pour des sites simples',
        "Courbe d'apprentissage plus raide",
        'Personnalisation du design limitée',
        'Les modèles peuvent sembler génériques',
        'Nécessite des connaissances WP pour les modifications avancées',
      ],
      ['10–30 min', '10–40 min', '5–15 min', '10–30 min', '30–90 min'],
      ['Puissant', 'Bon', 'Basique', 'Moyen', 'Puissant'],
      ['Élevée', 'Maximale', 'Faible', 'Moyenne', 'Élevée'],
      ['Nombreuses', 'Moyennes', 'Basiques', 'Moyennes', 'Nombreuses'],
      ['Facile', 'Difficile', 'Facile', 'Facile', 'Moyen'],
    ),
  },
  servicesTable: {
    title: 'Comparer les créateurs de sites web avec IA',
    subtitle: 'Classement éditorial selon une méthodologie unifiée.',
    footerText:
      "Nous créons un site test dans chaque constructeur et comparons la convivialité, la qualité du résultat et les possibilités d'édition.",
    service: 'Service',
    type: 'Type de site',
    speed: 'Vitesse',
    seo: 'SEO',
    flexibility: 'Flexibilité',
    integrations: 'Intégrations',
    price: 'Prix',
  },
  comparison: {
    title: 'Créateur avec IA ou studio web : que choisir ?',
    subtitle:
      'Comparaison factuelle par délais, coût, révisions et contrôle du résultat.',
    intro: '',
    feature: 'Caractéristique',
    studio: 'Studio web',
    ai: 'Créateur avec IA',
    rows: {
      time: {
        name: 'Délai de lancement',
        studio: '2–8 semaines',
        ai: '5–30 minutes',
      },
      cost: {
        name: 'Coût typique',
        studio: '2 000 – 10 000+ €',
        ai: '0 – 30 €/mois',
      },
      revisions: {
        name: 'Révisions',
        studio: 'Payantes ou limitées',
        ai: 'Illimitées, instantanées',
      },
      maintenance: {
        name: 'Support continu',
        studio: 'Contrat séparé',
        ai: 'Généralement inclus',
      },
      control: {
        name: 'Contrôle du contenu',
        studio: 'Via le développeur',
        ai: 'Autonomie complète',
      },
    },
  },
  methodology: {
    label: 'Confiance & transparence',
    title: 'Comment nous évaluons les créateurs de sites web avec IA',
    description:
      'Nous ne réécrivons pas simplement les promesses marketing. Nous testons réellement chaque créateur avec IA : nous créons des sites, vérifions la prise en main et comparons les résultats côte à côte.',
    criteria: [],
    disclaimer: '',
    cards: [
      {
        title: 'Tests en conditions réelles',
        desc: 'Nous créons de vrais sites et comparons les résultats sur chaque plateforme',
      },
      {
        title: 'Critères cohérents',
        desc: 'Chaque créateur de sites avec IA est évalué selon le même ensemble de critères',
      },
      {
        title: 'Avantages & inconvénients, sans blabla',
        desc: 'Des évaluations honnêtes avec des forces et faiblesses claires',
      },
      {
        title: 'Classement impartial',
        desc: 'Les classements reflètent la qualité, pas les sponsorings',
      },
    ],
  },
  faq: {
    title: 'Questions fréquentes sur les créateurs de sites web avec IA',
    subtitle: "Tout ce qu'il faut savoir pour créer un site web avec l'IA",
    items: [
      {
        q: "Qu'est-ce qu'un créateur de site web avec IA ?",
        a: "Un créateur de site web avec IA génère un brouillon à partir d'un prompt court. Il crée la structure, des textes de base et le design, puis vous personnalisez et publiez.\n\n• Crée des pages comme Accueil, À propos, Services, Contact\n• Éditeur visuel no-code pour les modifications\n• Mise en place plus rapide qu'une création manuelle\n• Idéal pour lancer vite et tester des idées",
      },
      {
        q: 'Comment un créateur avec IA génère-t-il un site ?',
        a: "Le plus souvent : vous décrivez le projet, l'IA génère une première version, puis vous l'affinez avant publication.\n\n• Indiquez votre niche, vos objectifs et vos préférences de style\n• L'IA génère la mise en page, les sections et le texte\n• Vous modifiez les blocs, polices, images et navigation\n• Vous connectez un domaine et publiez",
      },
      {
        q: 'Ai-je besoin de compétences en code ou en design ?',
        a: 'Non. Ces outils sont pensés pour les débutants et fonctionnent avec un éditeur visuel no-code.\n\n• Modifiez texte et sections comme dans un page builder\n• Ajustez le style sans programmer\n• Plus vos consignes sont claires (objectif, audience, ton), meilleur est le résultat',
      },
      {
        q: 'Les sites créés par IA sont-ils bons pour le SEO ?',
        a: "Oui, si vous optimisez le contenu et les réglages SEO de base. L'IA accélère le départ, mais le classement dépend de la qualité et de la structure.\n\n• Titres de page et meta descriptions uniques\n• Hiérarchie H1/H2 claire et maillage interne\n• Contenu utile, non dupliqué\n• Chargement rapide et bonne expérience mobile",
      },
      {
        q: "Puis-je créer un site multi-pages avec l'IA ?",
        a: "Oui. La plupart des outils génèrent des sites multi-pages avec menu et pages standard.\n\n• Accueil, À propos, Services, Contact, Blog (selon l'outil)\n• Menu et liens internes\n• Édition et mises à jour faciles après lancement",
      },
      {
        q: 'Puis-je connecter mon domaine et modifier le site plus tard ?',
        a: 'Oui. La plupart supportent les domaines personnalisés et les modifications après publication.\n\n• Connecter un domaine et activer le SSL\n• Mettre à jour les pages à tout moment\n• Publier les changements instantanément',
      },
      {
        q: 'Quel créateur avec IA est le meilleur pour débuter ?',
        a: "Cela dépend de la simplicité de l'éditeur et de la qualité du résultat par défaut pour vos besoins.\n\n• Onboarding simple et éditeur clair\n• Bons presets de design et flexibilité\n• Réglages SEO faciles à contrôler\n• Consultez notre section Rating pour des choix adaptés aux débutants",
      },
      {
        q: 'IA vs agence web : quelle différence ?',
        a: 'Les créateurs avec IA sont plus rapides et moins chers ; une agence apporte stratégie et sur-mesure.\n\n• IA : lancement en minutes, édition en autonomie, coût plus faible\n• Agence : design sur mesure, branding, fonctionnalités avancées, support\n• Utilisez le tableau comparatif ci-dessus pour choisir la meilleure option',
      },
    ],
  },
  trust: {
    editorial: {
      title: 'Politique éditoriale',
      desc: "Avis d'experts indépendants basés sur des tests réels.",
    },
    affiliate: {
      title: "Divulgation d'affiliation",
      desc: 'Nous pouvons recevoir une commission si vous achetez via nos liens, sans frais supplémentaires pour vous.',
    },
    noPaid: {
      title: 'Pas de placements payants',
      desc: "Nous n'acceptons pas d'argent pour des positions plus élevées.",
    },
  },
  cta: {
    title: "Essayez de créer un site avec l'IA maintenant",
    subtitle:
      "Choisissez un constructeur dans notre classement et lancez votre site en quelques minutes. C'est gratuit et ne nécessite aucune compétence.",
    button: "Créer un site avec l'IA",
    badge: 'Démarrage gratuit',
    benefits: ['Sans programmation', 'Prêt en 5 minutes', 'Démarrage gratuit'],
    socialProof: '',
  },
  newsletter: {
    title: 'Restez informé',
    subtitle:
      'Recevez les dernières offres et articles utiles sur les créateurs de sites avec IA',
    placeholder: 'Entrez votre email',
    button: "S'abonner",
    consent: "J'accepte le traitement des données personnelles",
    consentRequired: 'Veuillez accepter le consentement pour vous abonner',
    successMessage:
      'Vous êtes abonné avec succès ! Vérifiez votre email pour confirmer.',
    privacy: 'Désabonnement possible à tout moment.',
    success: 'Abonné !',
  },
  blog: {
    title: 'Derniers articles',
    subtitle: 'Actualités et articles sur le développement web avec IA',
    readMore: 'Lire la suite',
  },
})

// ═══════════════════════════════════════════════════════
// PORTUGUESE (BR)
// ═══════════════════════════════════════════════════════
const ptBr: typeof en = makeLang({
  seo: {
    title: 'Melhores criadores de sites com IA — comparação, reviews e preços',
    description:
      'Descubra e compare criadores de sites com IA. Reviews independentes, recursos, preços e prós/contras para escolher a melhor opção.',
  },
  nav: {
    rating: 'Ranking',
    comparison: 'Comparação',
    howItWorks: 'Como funciona',
    faq: 'Perguntas frequentes',
    start: 'Avaliar serviços',
  },
  hero: {
    h1: 'Os melhores criadores de sites com IA: ranking e comparação',
    subtitle:
      'Comparamos construtores de sites com IA: qual é mais fácil, qual dá melhores resultados e qual se adapta às suas necessidades.',
    description1:
      'Testamos criadores de sites com IA na prática e avaliamos por facilidade de uso, qualidade do design, ferramentas de SEO, preço e velocidade de publicação.',
    description2: '',
    cta: 'Ver ranking',
    compare: 'Comparar serviços',
  },
  whatIsAi: {
    title: 'Por que as pessoas criam sites com IA',
    subtitle:
      'Criadores de sites com IA ficaram populares porque são rápidos, simples e acessíveis para qualquer pessoa.',
    structure: {
      title: 'Pronto em minutos',
      desc: 'A IA gera um primeiro rascunho a partir de um prompt curto — sem semanas de espera.',
    },
    content: {
      title: 'Mais barato que uma agência',
      desc: 'Fácil de começar — o preço depende do plano.',
    },
    edit: {
      title: 'Simples e claro',
      desc: 'Edite de forma visual com um editor no-code simples.',
    },
    hosting: {
      title: 'Para a maioria das necessidades',
      desc: 'Sites de serviços, portfólios, páginas de negócios, sites multipáginas — a IA cobre os casos mais comuns.',
    },
  },
  howItWorks: {
    title: 'Como a IA cria seu site',
    subtitle:
      'A IA monta estrutura, textos e design em minutos — você só ajusta os detalhes e publica.',
    steps: [
      {
        title: 'Descreva o site que você precisa',
        desc: 'Informe seu tipo de negócio, público-alvo e objetivos do site.',
      },
      {
        title: 'A IA gera um site pronto',
        desc: 'O builder cria design, textos e estrutura em minutos.',
      },
      {
        title: 'Deixe com a sua cara',
        desc: 'Edite texto, imagens e estilo em um editor visual.',
      },
      {
        title: 'Conecte o domínio e publique',
        desc: 'Vincule seu endereço e o site fica no ar na hora.',
      },
    ],
  },
  topServices: {
    ...en.topServices,
    title: 'Ranking dos melhores criadores de sites com IA',
    subtitle:
      'Avaliação editorial com metodologia unificada: testamos os serviços na prática e avaliamos os resultados.',
    footerText:
      'Criamos um site de teste em cada construtor e comparamos a usabilidade, a qualidade do resultado e as capacidades de edição.',
    ratingDisclaimer:
      'Pontuação editorial baseada em funcionalidades, usabilidade e valor. Não são avaliações de usuários.',
    visitSite: 'Testar grátis',
    labels: {
      bestFor: 'Melhor para:',
      pros: 'Vantagens:',
      cons: 'Desvantagens:',
      details: 'Ver detalhes',
      hide: 'Ocultar detalhes',
      seo: 'Otimização de SEO',
      flexibility: 'Flexibilidade',
      integrations: 'Integrações',
      speed: 'Tempo de criação',
      difficulty: 'Dificuldade de criação',
    },
    services: svc(
      ['Universal', 'Design', 'Velocidade', 'Custo-benefício', 'WordPress'],
      [
        'Negócios em geral',
        'Portfólios e landing pages',
        'Serviços locais',
        'Começar gastando pouco',
        'Sites em WordPress',
      ],
      [
        ['Alta flexibilidade', 'SEO forte', 'Muitas integrações'],
        ['Flexibilidade máxima', 'Bom SEO', 'Integrações médias'],
        ['Geração rápida', 'SEO básico', 'Integrações básicas'],
        ['Flexibilidade média', 'SEO médio', 'Integrações médias'],
        ['Alta flexibilidade', 'SEO forte', 'Muitas integrações'],
      ],
      [
        'Pode ser complexo para sites simples',
        'Curva de aprendizado mais íngreme',
        'Personalização de design limitada',
        'Templates podem parecer genéricos',
        'Requer conhecimento de WP para edições profundas',
      ],
      ['10–30 min', '10–40 min', '5–15 min', '10–30 min', '30–90 min'],
      ['Forte', 'Bom', 'Básico', 'Médio', 'Forte'],
      ['Alta', 'Máxima', 'Baixa', 'Média', 'Alta'],
      ['Muitas', 'Médias', 'Básicas', 'Médias', 'Muitas'],
      ['Fácil', 'Difícil', 'Fácil', 'Fácil', 'Intermediária'],
    ),
  },
  servicesTable: {
    title: 'Comparar criadores de sites com IA',
    subtitle: 'Avaliação editorial com metodologia unificada.',
    footerText:
      'Criamos um site de teste em cada construtor e comparamos a usabilidade, a qualidade do resultado e as capacidades de edição.',
    service: 'Serviço',
    type: 'Tipo de site',
    speed: 'Velocidade',
    seo: 'SEO',
    flexibility: 'Flexibilidade',
    integrations: 'Integrações',
    price: 'Preço',
  },
  comparison: {
    title: 'Criador com IA ou estúdio web: o que escolher?',
    subtitle:
      'Comparação factual por prazos, custo, revisões e controle do resultado.',
    intro: '',
    feature: 'Característica',
    studio: 'Estúdio web',
    ai: 'Criador com IA',
    rows: {
      time: {
        name: 'Prazo de lançamento',
        studio: '2–8 semanas',
        ai: '5–30 minutos',
      },
      cost: {
        name: 'Custo típico',
        studio: 'R$10.000 – R$50.000+',
        ai: 'R$0 – R$150/mês',
      },
      revisions: {
        name: 'Revisões',
        studio: 'Pagas ou limitadas',
        ai: 'Ilimitadas, instantâneas',
      },
      maintenance: {
        name: 'Suporte contínuo',
        studio: 'Contrato separado',
        ai: 'Geralmente incluído',
      },
      control: {
        name: 'Controle do conteúdo',
        studio: 'Através do desenvolvedor',
        ai: 'Autoatendimento completo',
      },
    },
  },
  methodology: {
    label: 'Confiança e transparência',
    title: 'Como avaliamos criadores de sites com IA',
    description:
      'A gente não só reescreve promessas de marketing. Testamos de verdade cada criador com IA: criamos sites, checamos a facilidade de uso e comparamos os resultados lado a lado.',
    criteria: [],
    disclaimer: '',
    cards: [
      {
        title: 'Testes na prática',
        desc: 'Criamos sites reais e comparamos os resultados em cada plataforma',
      },
      {
        title: 'Critérios consistentes',
        desc: 'Cada criador com IA é avaliado com o mesmo conjunto de fatores',
      },
      {
        title: 'Prós e contras, sem enrolação',
        desc: 'Avaliações honestas com pontos fortes e fracos bem claros',
      },
      {
        title: 'Ranking imparcial',
        desc: 'Os rankings refletem qualidade, não patrocínios',
      },
    ],
  },
  faq: {
    title: 'Perguntas frequentes sobre criadores de sites com IA',
    subtitle: 'Tudo o que você precisa saber para criar sites com IA',
    items: [
      {
        q: 'O que é um criador de sites com IA?',
        a: 'Um criador de sites com IA gera um rascunho a partir de um prompt curto. Ele cria estrutura, textos básicos e design; depois você personaliza e publica.\n\n• Cria páginas como Início, Sobre, Serviços, Contato\n• Editor visual no-code para alterações\n• Configuração mais rápida do que criar manualmente\n• Ótimo para lançar rápido e testar ideias',
      },
      {
        q: 'Como um criador com IA cria um site?',
        a: 'O fluxo costuma ser: você descreve o projeto, a IA gera um rascunho e você ajusta antes de publicar.\n\n• Informe nicho, objetivos e preferências de estilo\n• A IA gera layout, seções e textos\n• Você edita blocos, fontes, imagens e navegação\n• Conecta um domínio e publica',
      },
      {
        q: 'Preciso saber programar ou ter noções de design?',
        a: 'Não. Esses criadores são feitos para iniciantes e funcionam com um editor visual no-code.\n\n• Edite texto e seções como em um page builder\n• Ajuste estilos sem programar\n• Quanto mais claro o input (objetivo, público, tom), melhor o resultado',
      },
      {
        q: 'Sites feitos com IA são bons para SEO?',
        a: 'Sim — se você revisar o conteúdo e as configurações básicas de SEO. A IA acelera o início, mas o ranking depende de qualidade e estrutura.\n\n• Títulos de página e meta descriptions únicos\n• Estrutura H1/H2 clara e links internos\n• Conteúdo útil, sem duplicações\n• Carregamento rápido e boa experiência no celular',
      },
      {
        q: 'Posso criar um site com várias páginas usando IA?',
        a: 'Sim. A maioria gera sites multi-páginas com menu e páginas padrão.\n\n• Início, Sobre, Serviços, Contato, Blog (depende do criador)\n• Menu e links internos\n• Edição e atualizações fáceis após o lançamento',
      },
      {
        q: 'Posso conectar meu próprio domínio e editar depois?',
        a: 'Sim. A maioria suporta domínio personalizado e edições contínuas após publicar.\n\n• Conectar domínio e habilitar SSL\n• Atualizar páginas a qualquer momento\n• Publicar mudanças instantaneamente',
      },
      {
        q: 'Qual criador de sites com IA é melhor para iniciantes?',
        a: 'Depende de quão simples é o editor e da qualidade do resultado padrão para o seu caso.\n\n• Onboarding simples e editor claro\n• Bons presets de design e flexibilidade\n• SEO fácil de controlar\n• Veja nossa seção Rating para opções amigáveis para iniciantes',
      },
      {
        q: 'IA vs contratar um estúdio: qual a diferença?',
        a: 'A IA é mais rápida e barata; estúdios oferecem estratégia e trabalho sob medida.\n\n• Criador com IA: lançamento em minutos, edição por conta própria, menor custo\n• Estúdio: design personalizado, branding, recursos avançados, suporte\n• Use a tabela comparativa acima para escolher a melhor opção',
      },
    ],
  },
  trust: {
    editorial: {
      title: 'Política editorial',
      desc: 'Avaliações independentes de especialistas baseadas em testes reais.',
    },
    affiliate: {
      title: 'Divulgação de afiliados',
      desc: 'Podemos receber uma comissão se você comprar através dos nossos links, sem custo adicional para você.',
    },
    noPaid: {
      title: 'Sem posicionamentos pagos',
      desc: 'Não aceitamos dinheiro por posições mais altas no ranking.',
    },
  },
  cta: {
    title: 'Experimente criar um site com IA agora mesmo',
    subtitle:
      'Escolha um construtor do nosso ranking e lance seu site em minutos. É grátis e não requer habilidades.',
    button: 'Criar site com IA',
    badge: 'Início gratuito',
    benefits: ['Sem programação', 'Pronto em 5 minutos', 'Início gratuito'],
    socialProof: '',
  },
  newsletter: {
    title: 'Fique por dentro',
    subtitle:
      'Receba as últimas ofertas e artigos úteis sobre criadores de sites com IA',
    placeholder: 'Digite seu email',
    button: 'Inscrever-se',
    consent: 'Concordo com o processamento de dados pessoais',
    consentRequired: 'Por favor, aceite o consentimento para se inscrever',
    successMessage:
      'Você se inscreveu com sucesso! Verifique seu email para confirmar.',
    privacy: 'Cancele a qualquer momento.',
    success: 'Inscrito!',
  },
  blog: {
    title: 'Últimos artigos',
    subtitle: 'Notícias e artigos sobre desenvolvimento web com IA',
    readMore: 'Ler mais',
  },
})

// ═══════════════════════════════════════════════════════
// ITALIAN
// ═══════════════════════════════════════════════════════
const it: typeof en = makeLang({
  seo: {
    title: 'Migliori website builder con IA — confronto, recensioni e prezzi',
    description:
      'Scopri e confronta i website builder con IA. Recensioni indipendenti, funzioni, prezzi, pro e contro per scegliere quello giusto.',
  },
  nav: {
    rating: 'Classifica',
    comparison: 'Confronto',
    howItWorks: 'Come funziona',
    faq: 'Domande frequenti',
    start: 'Recensisci servizi',
  },
  hero: {
    h1: 'I migliori creatori di siti con IA: classifica e confronto',
    subtitle:
      'Confrontiamo i costruttori di siti con IA: qual è il più semplice, quale dà i migliori risultati e quale si adatta alle tue esigenze.',
    description1:
      "Testiamo i creatori di siti con IA in modo pratico e li valutiamo per facilità d'uso, qualità del design, strumenti SEO, prezzo e velocità di pubblicazione.",
    description2: '',
    cta: 'Vedi la classifica',
    compare: 'Confronta i servizi',
  },
  whatIsAi: {
    title: "Perché le persone creano siti con l'IA",
    subtitle:
      'I creatori di siti con IA sono diventati popolari perché sono veloci, semplici e accessibili a tutti.',
    structure: {
      title: 'Pronto in pochi minuti',
      desc: "L'IA genera una prima bozza da un prompt breve — senza settimane di attesa.",
    },
    content: {
      title: 'Più economico di uno studio',
      desc: 'Facile da iniziare — il prezzo dipende dal piano.',
    },
    edit: {
      title: 'Semplice e chiaro',
      desc: 'Modifica tutto in modo visuale con un editor no-code semplice.',
    },
    hosting: {
      title: 'Per la maggior parte delle esigenze',
      desc: "Siti di servizi, portfolio, pagine business, siti multipagina — l'IA copre i casi più comuni.",
    },
  },
  howItWorks: {
    title: "Come l'IA crea il tuo sito",
    subtitle:
      "L'IA crea la struttura, i testi e il design in pochi minuti — tu solo finisci e pubblichi.",
    steps: [
      {
        title: 'Descrivi il sito di cui hai bisogno',
        desc: 'Indica settore, pubblico e obiettivi del sito.',
      },
      {
        title: "L'IA genera un sito pronto",
        desc: 'Il builder crea design, testi e struttura in pochi minuti.',
      },
      {
        title: 'Rendilo tuo',
        desc: 'Modifica testi, immagini e stile con un editor visuale.',
      },
      {
        title: 'Collega il dominio e pubblica',
        desc: "Collega l'indirizzo web e il sito va online subito.",
      },
    ],
  },
  topServices: {
    ...en.topServices,
    title: 'Classifica dei migliori creatori di siti web con IA',
    subtitle:
      'Valutazione editoriale con metodologia unificata: testiamo i servizi in modo pratico e valutiamo i risultati.',
    footerText:
      'Creiamo un sito di prova in ogni costruttore e confrontiamo usabilità, qualità del risultato e capacità di modifica.',
    ratingDisclaimer:
      'Punteggio editoriale basato su funzionalità, usabilità e valore. Non sono recensioni degli utenti.',
    visitSite: 'Prova gratis',
    labels: {
      bestFor: 'Ideale per:',
      pros: 'Vantaggi:',
      cons: 'Svantaggi:',
      details: 'Mostra dettagli',
      hide: 'Nascondi dettagli',
      seo: 'Ottimizzazione SEO',
      flexibility: 'Flessibilità',
      integrations: 'Integrazioni',
      speed: 'Tempo di creazione',
      difficulty: 'Difficoltà di creazione',
    },
    services: svc(
      ['Universale', 'Design', 'Velocità', 'Qualità/Prezzo', 'WordPress'],
      [
        'Business generico',
        'Portfolio e landing page',
        'Servizi locali',
        'Partenza economica',
        'Siti WordPress',
      ],
      [
        ['Alta flessibilità', 'SEO potente', 'Molte integrazioni'],
        ['Flessibilità massima', 'Buon SEO', 'Integrazioni medie'],
        ['Generazione rapida', 'SEO base', 'Integrazioni base'],
        ['Flessibilità media', 'SEO medio', 'Integrazioni medie'],
        ['Alta flessibilità', 'SEO potente', 'Molte integrazioni'],
      ],
      [
        'Può essere complesso per siti semplici',
        'Curva di apprendimento più ripida',
        'Personalizzazione del design limitata',
        'I template possono sembrare generici',
        'Richiede conoscenze WP per modifiche avanzate',
      ],
      ['10–30 min', '10–40 min', '5–15 min', '10–30 min', '30–90 min'],
      ['Potente', 'Buono', 'Base', 'Medio', 'Potente'],
      ['Alta', 'Massima', 'Bassa', 'Media', 'Alta'],
      ['Molte', 'Medie', 'Base', 'Medie', 'Molte'],
      ['Facile', 'Difficile', 'Facile', 'Facile', 'Media'],
    ),
  },
  servicesTable: {
    title: 'Confronto dei creatori di siti web con IA',
    subtitle: 'Valutazione editoriale con metodologia unificata.',
    footerText:
      'Creiamo un sito di prova in ogni costruttore e confrontiamo usabilità, qualità del risultato e capacità di modifica.',
    service: 'Servizio',
    type: 'Tipo di sito',
    speed: 'Velocità',
    seo: 'SEO',
    flexibility: 'Flessibilità',
    integrations: 'Integrazioni',
    price: 'Prezzo',
  },
  comparison: {
    title: 'Builder IA vs web studio: qual è la differenza?',
    subtitle:
      'Un confronto fattuale per tempi, costi, revisioni e controllo del risultato.',
    intro: '',
    feature: 'Caratteristica',
    studio: 'Web studio',
    ai: 'Builder IA',
    rows: {
      time: {
        name: 'Tempo al lancio',
        studio: '2–8 settimane',
        ai: '5–30 minuti',
      },
      cost: {
        name: 'Costo tipico',
        studio: '$2.000–$10.000+',
        ai: '$0–$30/mese',
      },
      revisions: {
        name: 'Revisioni',
        studio: 'A pagamento o limitate',
        ai: 'Illimitate, istantanee',
      },
      maintenance: {
        name: 'Supporto continuo',
        studio: 'Contratto separato',
        ai: 'Di solito incluso',
      },
      control: {
        name: 'Controllo contenuti',
        studio: 'Tramite sviluppatore',
        ai: 'Autogestione completa',
      },
    },
  },
  methodology: {
    label: 'Fiducia e trasparenza',
    title: 'Come valutiamo i creatori di siti con IA',
    description:
      "Non ci limitiamo a riscrivere promesse di marketing. Testiamo davvero ogni creatore con IA: creiamo siti, verifichiamo la facilità d'uso e confrontiamo i risultati fianco a fianco.",
    criteria: [],
    disclaimer: '',
    cards: [
      {
        title: 'Test pratici',
        desc: 'Creiamo siti reali e confrontiamo i risultati su ogni piattaforma',
      },
      {
        title: 'Criteri coerenti',
        desc: 'Ogni creatore con IA viene valutato con lo stesso insieme di fattori',
      },
      {
        title: 'Pro e contro, senza fronzoli',
        desc: 'Valutazioni oneste con punti di forza e debolezze chiari',
      },
      {
        title: 'Classifica imparziale',
        desc: 'Le classifiche riflettono la qualità, non le sponsorizzazioni',
      },
    ],
  },
  faq: {
    title: 'Domande frequenti sui website builder con IA',
    subtitle: "Tutto quello che devi sapere per creare siti web con l'IA",
    items: [
      {
        q: "Che cos'è un website builder con IA?",
        a: 'Un website builder con IA crea una bozza di sito da un prompt breve. Genera struttura, testi base e design, poi tu personalizzi e pubblichi.\n\n• Crea pagine come Home, Chi siamo, Servizi, Contatti\n• Editor visuale no-code per le modifiche\n• Setup più veloce rispetto alla creazione manuale\n• Utile per lanci rapidi e per testare idee',
      },
      {
        q: 'Come crea un sito un builder con IA?',
        a: "Di solito il flusso è: descrivi il progetto, l'IA genera una bozza e tu la rifinisci prima di pubblicare.\n\n• Inserisci nicchia, obiettivi e preferenze di stile\n• L'IA genera layout, sezioni e testo\n• Modifichi blocchi, font, immagini e navigazione\n• Colleghi un dominio e pubblichi",
      },
      {
        q: 'Servono competenze di codice o design?',
        a: 'No. I builder con IA sono pensati per principianti e funzionano con un editor visuale no-code.\n\n• Modifica testo e sezioni come in un page builder\n• Regola lo stile senza programmare\n• Input chiari (obiettivo, pubblico, tono) migliorano il risultato',
      },
      {
        q: 'I siti creati con IA sono buoni per la SEO?',
        a: "Sì — se curi contenuti e impostazioni SEO di base. L'IA accelera l'avvio, ma il ranking dipende da qualità e struttura.\n\n• Titoli pagina e meta description unici\n• Struttura H1/H2 chiara e link interni\n• Contenuti utili, non duplicati\n• Caricamento rapido e usabilità mobile",
      },
      {
        q: 'Posso creare un sito multipagina con IA?',
        a: 'Sì. La maggior parte genera siti multipagina con menu e pagine standard.\n\n• Home, Chi siamo, Servizi, Contatti, Blog (dipende dal builder)\n• Menu e collegamenti interni\n• Modifiche e aggiornamenti semplici dopo il lancio',
      },
      {
        q: 'Posso collegare il mio dominio e modificare il sito in seguito?',
        a: "Sì. Quasi tutti supportano domini personalizzati e modifiche continue dopo la pubblicazione.\n\n• Collega dominio e abilita SSL\n• Aggiorna le pagine quando vuoi\n• Pubblica le modifiche all'istante",
      },
      {
        q: 'Qual è il migliore per principianti?',
        a: "Dipende da quanto è semplice l'editor e dalla qualità del risultato iniziale per le tue esigenze.\n\n• Onboarding semplice e editor chiaro\n• Buoni preset di design e flessibilità\n• Impostazioni SEO facili da controllare\n• Vedi la sezione Rating per scelte adatte ai principianti",
      },
      {
        q: 'Builder IA vs web studio: qual è la differenza?',
        a: 'I builder IA sono più rapidi ed economici; gli studi offrono strategia e personalizzazione avanzata.\n\n• Builder IA: lancio in minuti, editing autonomo, costo più basso\n• Studio: design su misura, branding, funzioni avanzate, supporto\n• Usa la tabella di confronto sopra per scegliere',
      },
    ],
  },
  trust: {
    editorial: {
      title: 'Politica editoriale',
      desc: 'Recensioni indipendenti di esperti basate su test reali.',
    },
    affiliate: {
      title: 'Informativa sugli affiliati',
      desc: 'Potremmo ricevere una commissione se acquisti tramite i nostri link.',
    },
    noPaid: {
      title: 'Nessun posizionamento a pagamento',
      desc: 'Non accettiamo denaro per posizioni più alte.',
    },
  },
  cta: {
    title: "Prova a creare un sito con l'IA adesso",
    subtitle:
      'Scegli un costruttore dalla nostra classifica e lancia il tuo sito in pochi minuti. È gratuito e non richiede competenze.',
    button: 'Crea un sito con IA',
    badge: 'Inizio gratuito',
    benefits: ['Senza programmazione', 'Pronto in 5 minuti', 'Inizio gratuito'],
    socialProof: '',
  },
  newsletter: {
    title: 'Resta aggiornato',
    subtitle:
      'Ricevi le ultime offerte e articoli utili sui creatori di siti con IA',
    placeholder: 'Inserisci la tua email',
    button: 'Iscriviti',
    consent: 'Acconsento al trattamento dei dati personali',
    consentRequired: 'Per favore accetta il consenso per iscriverti',
    successMessage:
      'Ti sei iscritto con successo! Controlla la tua email per confermare.',
    privacy: 'Cancellazione possibile in qualsiasi momento.',
    success: 'Iscritto!',
  },
  blog: {
    title: 'Ultimi articoli',
    subtitle: 'Notizie e articoli sullo sviluppo web con IA',
    readMore: 'Leggi di più',
  },
})

// ═══════════════════════════════════════════════════════
// DUTCH
// ═══════════════════════════════════════════════════════
const nl: typeof en = makeLang({
  seo: {
    title: 'Beste AI-website builders — vergelijking, reviews & prijzen',
    description:
      'Ontdek en vergelijk AI-website builders. Onafhankelijke reviews, functies, prijzen en voor- en nadelen — kies de juiste builder.',
  },
  nav: {
    rating: 'Beoordeling',
    comparison: 'Vergelijking',
    howItWorks: 'Hoe het werkt',
    faq: 'Veelgestelde vragen',
    start: 'Diensten beoordelen',
  },
  hero: {
    h1: 'De beste websitebouwers met AI: beoordeling & vergelijking',
    subtitle:
      'We vergelijken websitebouwers met AI: welke is het makkelijkst, welke geeft de beste resultaten en welke past bij jouw behoeften.',
    description1:
      'We testen websitebouwers met AI in de praktijk en beoordelen ze op gebruiksgemak, designkwaliteit, SEO-tools, prijs en publicatiesnelheid.',
    description2: '',
    cta: 'Bekijk beoordeling',
    compare: 'Bouwers vergelijken',
  },
  whatIsAi: {
    title: 'Waarom mensen websites bouwen met AI',
    subtitle:
      'Websitebouwers met AI zijn populair omdat ze snel, eenvoudig en voor iedereen toegankelijk zijn.',
    structure: {
      title: 'Klaar in minuten',
      desc: 'AI maakt een eerste opzet op basis van een korte prompt — zonder weken wachten.',
    },
    content: {
      title: 'Goedkoper dan een bureau',
      desc: 'Betaalbaar om te starten — de prijs hangt af van het abonnement.',
    },
    edit: {
      title: 'Simpel en duidelijk',
      desc: 'Bewerk visueel met een eenvoudige no-code editor.',
    },
    hosting: {
      title: 'Voor de meeste behoeften',
      desc: "Diensten-sites, portfolio's, zakelijke pagina's, websites met meerdere pagina's — AI dekt de meest voorkomende toepassingen.",
    },
  },
  howItWorks: {
    title: 'Zo maakt AI je website',
    subtitle:
      'AI zet structuur, teksten en design in minuten klaar — jij verfijnt de details en publiceert.',
    steps: [
      {
        title: 'Beschrijf de site die je nodig hebt',
        desc: 'Geef je branche, doelgroep en doelen door.',
      },
      {
        title: 'AI genereert een kant-en-klare site',
        desc: 'De builder maakt design, teksten en structuur in minuten.',
      },
      {
        title: 'Maak hem helemaal van jou',
        desc: 'Bewerk tekst, afbeeldingen en stijl in een visuele editor.',
      },
      {
        title: 'Koppel je domein en lanceer',
        desc: 'Link je webadres — je site staat direct online.',
      },
    ],
  },
  topServices: {
    ...en.topServices,
    title: 'Beoordeling van de beste websitebouwers met AI',
    subtitle:
      'Redactionele beoordeling met uniforme methodiek: we testen diensten in de praktijk en evalueren de resultaten.',
    footerText:
      'We bouwen een testsite in elke bouwer en vergelijken gebruiksgemak, resultaatkwaliteit en bewerkingsmogelijkheden.',
    ratingDisclaimer:
      'Redactionele score gebaseerd op functies, gebruiksgemak en waarde. Geen gebruikersrecensies.',
    visitSite: 'Gratis proberen',
    labels: {
      bestFor: 'Het beste voor:',
      pros: 'Voordelen:',
      cons: 'Nadelen:',
      details: 'Details tonen',
      hide: 'Details verbergen',
      seo: 'SEO-optimalisatie',
      flexibility: 'Flexibiliteit',
      integrations: 'Integraties',
      speed: 'Aanmaaksnelheid',
      difficulty: 'Aanmaakmoeilijkheid',
    },
    services: svc(
      ['Universeel', 'Design', 'Snelheid', 'Prijs/Kwaliteit', 'WordPress'],
      [
        'Universeel zakelijk',
        "Portfolio's en landingspagina's",
        'Lokale diensten',
        'Voordelig starten',
        'WordPress-sites',
      ],
      [
        ['Hoge flexibiliteit', 'Sterke SEO', 'Veel integraties'],
        ['Maximale flexibiliteit', 'Goede SEO', 'Gemiddelde integraties'],
        ['Snelle generatie', 'Basis-SEO', 'Basisintegraties'],
        [
          'Gemiddelde flexibiliteit',
          'Gemiddelde SEO',
          'Gemiddelde integraties',
        ],
        ['Hoge flexibiliteit', 'Sterke SEO', 'Veel integraties'],
      ],
      [
        'Kan overweldigend zijn voor eenvoudige sites',
        'Steilere leercurve',
        'Beperkte designaanpassing',
        'Templates kunnen generiek aanvoelen',
        'WP-kennis nodig voor diepgaande aanpassingen',
      ],
      ['10–30 min', '10–40 min', '5–15 min', '10–30 min', '30–90 min'],
      ['Sterk', 'Goed', 'Basis', 'Gemiddeld', 'Sterk'],
      ['Hoog', 'Maximaal', 'Laag', 'Gemiddeld', 'Hoog'],
      ['Veel', 'Gemiddeld', 'Basis', 'Gemiddeld', 'Veel'],
      ['Makkelijk', 'Moeilijk', 'Makkelijk', 'Makkelijk', 'Gemiddeld'],
    ),
  },
  servicesTable: {
    title: 'Websitebouwers met AI vergelijken',
    subtitle: 'Redactionele beoordeling met uniforme methodiek.',
    footerText:
      'We bouwen een testsite in elke bouwer en vergelijken gebruiksgemak, resultaatkwaliteit en bewerkingsmogelijkheden.',
    service: 'Dienst',
    type: 'Websitetype',
    speed: 'Snelheid',
    seo: 'SEO',
    flexibility: 'Flexibiliteit',
    integrations: 'Integraties',
    price: 'Prijs',
  },
  comparison: {
    title: 'Websitebouwer met AI of webbureau: wat kiezen?',
    subtitle:
      'Feitelijke vergelijking op basis van doorlooptijd, kosten, revisies en controle.',
    intro: '',
    feature: 'Kenmerk',
    studio: 'Webbureau',
    ai: 'Websitebouwer met AI',
    rows: {
      time: { name: 'Doorlooptijd', studio: '2–8 weken', ai: '5–30 minuten' },
      cost: {
        name: 'Typische kosten',
        studio: '€2.000 – €10.000+',
        ai: '€0 – €30/maand',
      },
      revisions: {
        name: 'Revisies',
        studio: 'Betaald of beperkt',
        ai: 'Onbeperkt, direct',
      },
      maintenance: {
        name: 'Doorlopend onderhoud',
        studio: 'Apart contract',
        ai: 'Meestal inbegrepen',
      },
      control: {
        name: 'Contentbeheer',
        studio: 'Via de ontwikkelaar',
        ai: 'Volledig zelfbediening',
      },
    },
  },
  methodology: {
    label: 'Vertrouwen & transparantie',
    title: 'Hoe wij websitebouwers met AI beoordelen',
    description:
      'We herschrijven niet alleen marketingclaims. We testen elke websitebouwer met AI echt: we bouwen sites, checken gebruiksgemak en vergelijken resultaten naast elkaar.',
    criteria: [],
    disclaimer: '',
    cards: [
      {
        title: 'Praktijktests',
        desc: 'We bouwen echte websites en vergelijken de resultaten per platform',
      },
      {
        title: 'Consistente criteria',
        desc: 'Elke websitebouwer met AI wordt beoordeeld op dezelfde set factoren',
      },
      {
        title: 'Plus- & minpunten, zonder fluff',
        desc: 'Eerlijke beoordelingen met duidelijke sterke en zwakke punten',
      },
      {
        title: 'Onpartijdige ranking',
        desc: 'Rankings weerspiegelen kwaliteit, niet sponsoring',
      },
    ],
  },
  faq: {
    title: 'Veelgestelde vragen over websitebouwers met AI',
    subtitle: 'Alles wat je moet weten over websites bouwen met AI',
    items: [
      {
        q: 'Wat is een websitebouwer met AI?',
        a: "Een websitebouwer met AI maakt een eerste website-concept op basis van een korte prompt. Hij genereert structuur, basisteksten en design; daarna pas je alles aan en publiceer je.\n\n• Maakt pagina's zoals Home, Over ons, Diensten, Contact\n• Visuele no-code editor voor wijzigingen\n• Sneller dan handmatig bouwen\n• Handig voor snelle lanceringen en het testen van ideeën",
      },
      {
        q: 'Hoe maakt een websitebouwer met AI een website?',
        a: 'Meestal werkt het zo: jij beschrijft je project, de AI maakt een concept en jij verfijnt het vóór publicatie.\n\n• Vul niche, doelen en stijlvoorkeuren in\n• AI genereert layout, secties en tekst\n• Je bewerkt blokken, lettertypes, afbeeldingen en navigatie\n• Domein koppelen en publiceren',
      },
      {
        q: 'Heb ik programmeer- of designskills nodig?',
        a: 'Nee. Websitebouwers met AI zijn voor beginners en werken met een visuele no-code editor.\n\n• Tekst en secties aanpassen zoals in een page builder\n• Stijl aanpassen zonder te programmeren\n• Hoe duidelijker je input (doel, doelgroep, tone of voice), hoe beter het resultaat',
      },
      {
        q: 'Zijn websites gebouwd met AI goed voor SEO?',
        a: 'Ja — als je content en basis-SEO instelt en aanscherpt. AI versnelt de start, maar rankings hangen af van kwaliteit en structuur.\n\n• Unieke paginatitels en meta descriptions\n• Duidelijke H1/H2-structuur en interne links\n• Nuttige, niet-gedupliceerde content\n• Snelle laadtijd en goede mobiele ervaring',
      },
      {
        q: "Kan ik met AI een website met meerdere pagina's maken?",
        a: "Ja. De meeste tools maken multipage sites met menu en standaardpagina's.\n\n• Home, Over, Diensten, Contact, Blog (afhankelijk van de tool)\n• Menu en interne links\n• Eenvoudig bewerken en updaten na livegang",
      },
      {
        q: 'Kan ik mijn eigen domein koppelen en later blijven bewerken?',
        a: "Ja. De meeste builders ondersteunen een eigen domein en doorlopende edits na publicatie.\n\n• Domein koppelen en SSL inschakelen\n• Pagina's altijd aanpassen in de editor\n• Wijzigingen direct publiceren",
      },
      {
        q: 'Welke websitebouwer met AI is het beste voor beginners?',
        a: 'Dat hangt af van hoe makkelijk de editor voelt en hoe goed de standaard-output is voor jouw behoeften.\n\n• Eenvoudige onboarding en duidelijke editor\n• Goede design-presets en flexibiliteit\n• SEO-instellingen die makkelijk te beheren zijn\n• Bekijk onze Rating-sectie voor beginner-vriendelijke keuzes',
      },
      {
        q: 'Websitebouwer met AI vs webbureau: wat is het verschil?',
        a: 'Websitebouwers met AI zijn sneller en goedkoper; bureaus bieden meer strategie en maatwerk.\n\n• Websitebouwer met AI: live in minuten, zelf bewerken, lagere kosten\n• Webbureau: maatwerkdesign, branding, geavanceerde functies, support\n• Gebruik de vergelijkingstabel hierboven om te kiezen',
      },
    ],
  },
  trust: {
    editorial: {
      title: 'Redactionele beleid',
      desc: 'Onafhankelijke expertbeoordelingen op basis van echte tests.',
    },
    affiliate: {
      title: 'Affiliate-vermelding',
      desc: 'We ontvangen mogelijk een commissie als je via onze links koopt, zonder extra kosten voor jou.',
    },
    noPaid: {
      title: 'Geen betaalde plaatsingen',
      desc: 'We accepteren geen geld voor hogere rankings.',
    },
  },
  cta: {
    title: 'Probeer nu een website te maken met AI',
    subtitle:
      'Kies een bouwer uit onze beoordeling en lanceer je site in minuten. Het is gratis en vereist geen vaardigheden.',
    button: 'Website maken met AI',
    badge: 'Gratis starten',
    benefits: [
      'Geen programmeren nodig',
      'Klaar in 5 minuten',
      'Gratis starten',
    ],
    socialProof: '',
  },
  newsletter: {
    title: 'Blijf op de hoogte',
    subtitle:
      'Ontvang de nieuwste aanbiedingen en nuttige artikelen over websitebouwers met AI',
    placeholder: 'Voer je e-mail in',
    button: 'Abonneren',
    consent: 'Ik ga akkoord met de verwerking van persoonsgegevens',
    consentRequired: 'Geef alsjeblieft toestemming om je te abonneren',
    successMessage:
      'Je bent succesvol geabonneerd! Controleer je e-mail voor bevestiging.',
    privacy: 'Op elk moment opzegbaar.',
    success: 'Geabonneerd!',
  },
  blog: {
    title: 'Laatste inzichten',
    subtitle: 'Nieuws en artikelen over webontwikkeling met AI',
    readMore: 'Lees meer',
  },
})

// ═══════════════════════════════════════════════════════
// TRANSLATIONS MAP
// ═══════════════════════════════════════════════════════
export const translations: Record<Language, typeof en> = {
  en,
  ru,
  ua,
  de,
  es,
  fr,
  'pt-br': ptBr,
  it,
  nl,
}

export default translations
